/* ==========================================================================
   Countertop Wizard
   Handles question state, persistence, conditional product filtering,
   recommended-qty math, per-product add-to-cart, and the sticky cart panel.
   ========================================================================== */
(function () {
  'use strict';

  const root = document.getElementById('ctw-root');
  if (!root) return;

  // -------------------- Product data + constants --------------------
  const PRODUCTS = (function () {
    try {
      const raw = JSON.parse(document.getElementById('ctw-products-data').textContent || '[]');
      // Dedupe — a product may appear in multiple source collections (e.g.,
      // Diamond Polishing Pads lives in both Abrasives and Power Tools).
      const seen = new Set();
      return raw.filter(p => {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
    } catch (e) { console.error('[CTW] Failed to parse product data', e); return []; }
  })();

  const STORAGE_KEY = 'ctw_state_v1';
  const SECTIONS = ['forms', 'mix', 'accessories', 'coloring', 'sealers', 'texturing', 'tools', 'sanding', 'brackets', 'grills'];

  // Default selected variants (by SKU substring or grit label) for multi-variant
  // products. Keyed by product handle. Used the first time we render a card so
  // the recommended grits are pre-checked.
  // Pre-checked variants for multi-variant products. Empty arrays = no defaults
  // (customer picks themselves).
  const MULTI_VARIANT_DEFAULTS = {
    'z-grit-disc-sand-paper': [],
    'diamond-disc': [],
  };

  // Sealer coverage rates (sq ft per unit / kit / bottle) — sourced from product
  // descriptions. Adjust here if SKUs / pack sizes change.
  const SEALER_COVERAGE = {
    'z-siacryl-14-sealer': 75,                  // 1 qt bottle, 3–4 coats → 75–100 sq ft
    'z-aqua-poxy-2-qt-epoxy-sealer': 75,        // 2 qt kit, 2–3 coats → 75–100 sq ft
    'z-aqua-thane-m35': 50,                     // 1.25 qt kit, 2 coats → 50–75 sq ft
    'z-aqua-thane-g40': 50,                     // 1.5 qt kit, 2 coats → 50–75 sq ft
    'z-counter-shine-polish': 200,              // 1 qt bottle, 1 coat → 200–250 sq ft
    // Ultra Z Poxy is variant-based: 1.5 gal → 40 sq ft, 3 gal → 80 sq ft
  };

  // Per-product custom "Recommended" label override (replaces the default "Recommended: N")
  const RECOMMENDED_LABEL_OVERRIDES = {
    'z-grit-disc-sand-paper': 'Recommended: 1 ea. 80, 120 and 220 grit',
    'diamond-disc': 'Recommended: 1 ea. 120 and 200 grit',
  };
  const ULTRAZPOXY_SQFT = { '1.5': 40, '3': 80 };

  // Form liner piece lengths in feet (from product descriptions). Used to calc
  // how many pieces a customer needs based on their profile (edge) footage.
  const FORM_LINER_LENGTH_FT = {
    'stone-form-liner-thin-insert': 7.5,
    'rock-face-form-liner': 8,
    'travertine-form-liner-thin-insert': 8,
    'heavy-chiseled-slate-liner': 8,
    'slate-form-liner-thin-insert': 7.5,
    'woodgrain-liner': 7,
  };

  // Coverage / quantity rules
  const COVERAGE = {
    // Bags of WHITE MIX per sq ft, by form type
    whiteMixSqftPerBag: { standard: 3.0, commercial: 1.9, euro: 5.1 },
    // Bags of LIQUI-CRETE (60 lb add-mix) per sq ft, by form type (not for euro)
    liquiCreteSqftPerBag: { standard: 3.9, commercial: 2.4 },
    // 1 roll of FG50 mesh covers 100 sq ft
    meshSqftPerRoll: 100,
    // 1 box of Z Clips covers ~33 sq ft
    clipsSqftPerBox: 33,
    // Construction screws per box
    screwsPerBox: 200,
    screwsPerFullPackage: 60,
    screwsPerHalfPackage: 30,
    screwsPerClipBox: 100,
    // Stain / dye coverage
    aquaSqftPerBottle: 75,
    // Form packages
    fullPackageFt: 60,
    halfPackageFt: 30,
    // Color-pack: 1 per bag of mix (assume same total bags as mix recommendation)
    // Terra-Tint: 2 scoops per bag (~10 bags/bucket at 2 scoops; range up to 4 scoops = ~5 bags/bucket)
    terraTintBagsPerBucket: 10,
  };

  // -------------------- Utility helpers --------------------
  const $ = (sel, parent) => (parent || root).querySelector(sel);
  const $$ = (sel, parent) => Array.from((parent || root).querySelectorAll(sel));

  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    return (window.Shopify && Shopify.formatMoney)
      ? Shopify.formatMoney(cents, (window.theme && theme.moneyFormat) || '${{amount}}')
      : '$' + (cents / 100).toFixed(2);
  }
  function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c]); }
  function debounce(fn, ms) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; }
  function num(v) { const n = parseFloat(v); return isNaN(n) ? 0 : n; }

  // Out-of-stock products are intentionally NOT filtered out: a wizard-curated
  // product that goes out of stock still appears (with the card's "Out of stock"
  // state + disabled Add button) instead of silently vanishing from its section.
  function byTag(tag) { return PRODUCTS.filter(p => p.tags && p.tags.indexOf(tag) !== -1); }
  function findByHandle(handle) { return PRODUCTS.find(p => p.handle === handle); }
  function findByTagSingle(tag) { return PRODUCTS.find(p => p.tags && p.tags.indexOf(tag) !== -1); }

  // -------------------- State + persistence --------------------
  const State = {
    answers: {
      profileFt: 0, backwallFt: 0, sqFt: 0,
      sink: '', faucetHoles: 0,
      useLocation: '', colorStyle: '',
      sealerFinish: '',
      textureEdge: '', textureTop: '',
      needFinishingTools: '',
      wetPolish: '', needBrackets: '', needGrill: ''
    },
    // cart = the MERGED view the wizard renders: authoritative server cart +
    // the local pending (not-yet-synced) picks overlaid. serverCart = the last
    // /cart.js payload, untouched. See recomputeMergedCart().
    cart: { items: [], total_price: 0, item_count: 0 },
    serverCart: { items: [], total_price: 0, item_count: 0 },
  };

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        Object.assign(State.answers, parsed.answers || {});
      }
    } catch (e) { /* ignore */ }
  }
  function saveState() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers: State.answers })); }
    catch (e) { /* quota/disabled */ }
  }
  function clearState() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    Object.assign(State.answers, {
      profileFt: 0, backwallFt: 0, sqFt: 0,
      sink: '', faucetHoles: 0,
      useLocation: '', colorStyle: '',
      sealerFinish: '',
      textureEdge: '', textureTop: '',
      needFinishingTools: '',
      wetPolish: '', needBrackets: '', needGrill: ''
    });
  }

  // -------------------- Cart API --------------------
  // Route every wizard cart call through the pristine native fetch the head
  // guard (snippets/ctw-cart-guard.liquid) exposes as window.__ctwFetch. That
  // keeps our calls out of the guard's third-party throttle and immune to any
  // app's fetch wrapper. Absolute URLs, always.
  // Once Shopify's Cloudflare "managed challenge" fires (HTTP 403 +
  // `cf-mitigated: challenge`), the ENTIRE cart-API session is locked until a
  // top-level navigation issues a fresh cf_clearance cookie. fetch/XHR cannot
  // solve that challenge, so every further add/remove/qty call would also 403
  // and only keep us locked longer. The moment we detect it ANYWHERE, we flip a
  // global lockout: stop firing cart requests entirely and surface the
  // "refresh to continue" banner. This is why, until the customer refreshes,
  // nothing cart-related works — we now make that explicit instead of silent.
  let _cartLockedOut = false;
  // The challenge has been observed as 403 AND as 429 — always with the
  // `cf-mitigated: challenge` response header, which is the reliable signal.
  function isChallengeResponse(r) {
    return !!r && (r.status === 403 || (r.headers && r.headers.get && r.headers.get('cf-mitigated') === 'challenge'));
  }
  function lockOutCart() {
    if (_cartLockedOut) return;
    _cartLockedOut = true;
    persistPendingAdds(); // snapshot unconfirmed adds so the refresh restores them
    showRateLimitBanner();
  }
  function challengeError() {
    const e = new Error('Shopify temporary rate limit'); e.code = 'CHALLENGE'; return e;
  }
  function cartFetch(url, opts) {
    if (_cartLockedOut) return Promise.reject(challengeError());
    const f = (typeof window !== 'undefined' && window.__ctwFetch) ? window.__ctwFetch : fetch;
    const abs = url.charAt(0) === '/' ? (window.location.origin + url) : url;
    return Promise.resolve(f(abs, opts)).then((res) => {
      if (isChallengeResponse(res)) { lockOutCart(); throw challengeError(); }
      return res;
    });
  }
  async function fetchCart() {
    try {
      const res = await cartFetch('/cart.js', { headers: { Accept: 'application/json', 'X-Ctw-Internal': '1' }, credentials: 'same-origin' });
      State.serverCart = await res.json();
      recomputeMergedCart();
    } catch (e) { console.error('[CTW] fetchCart failed', e); }
  }
  // Batched add: a SINGLE POST that adds one or more line items at once.
  // Using the batched format (items: [...]) instead of N separate POSTs
  // avoids tripping Cloudflare's bot-detection rules on the storefront.
  async function addItemsToCart(items, opts) {
    opts = opts || {};
    // Use FormData (multipart/form-data) — closer to the pattern Cloudflare
    // expects from a real browser form submission, less likely to trigger
    // bot-challenge rules than JSON.
    const fd = new FormData();
    items.forEach((it, i) => {
      fd.append(`items[${i}][id]`, it.id);
      fd.append(`items[${i}][quantity]`, it.quantity);
      // Hidden wizard-source marker on every wizard-added line. The leading
      // underscore keeps it out of the customer-facing cart/checkout, but it's
      // saved on the order line (admin + Admin API + Shopify Flow), so a Flow
      // "Order created" workflow can tag the order "Countertop Wizard". Rides
      // this existing /cart/add.js — no extra cart-API request. (Survives
      // express checkouts better than a cart attribute, which they drop.)
      fd.append(`items[${i}][properties][_ctw]`, '1');
    });
    // X-Ctw-Internal: marks this as a wizard call so the head guard's throttle
    // passes it straight through to the native fetch.
    const headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json', 'X-Ctw-Internal': '1' };
    // cartFetch throws a CHALLENGE error the instant Cloudflare's managed
    // challenge appears (and short-circuits once locked out), so we never need
    // to inspect the response for it here.
    const doFetch = () => cartFetch('/cart/add.js', { method: 'POST', headers, body: fd, credentials: 'same-origin', keepalive: !!opts.keepalive });
    let res = await doFetch();
    // Retry once on transient failures (network blip / cart session race / soft
    // rate limit). Wait long enough that the retry can't pile onto the same
    // rate-limit window that failed the first attempt.
    if (!res.ok && (res.status === 0 || res.status === 408 || res.status === 429 || res.status >= 500)) {
      await new Promise(r => setTimeout(r, 2500));
      res = await doFetch();
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = err.description || err.message || `Status ${res.status}${res.statusText ? ' ' + res.statusText : ''}`;
      console.error('[CTW] addItems failed', { items, status: res.status, body: err });
      throw new Error(msg);
    }
    return res.json();
  }

  // Back-compat single-item helper — now defers like every other add.
  function addToCart(variantId, quantity) {
    enqueueAdd([{ id: variantId, quantity: quantity }]);
    return Promise.resolve();
  }
  async function changeLine(line, quantity) {
    const res = await cartFetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json', 'X-Ctw-Internal': '1' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    });
    State.serverCart = await res.json();
    recomputeMergedCart();
  }
  async function clearCart() {
    const res = await cartFetch('/cart/clear.js', {
      method: 'POST', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json', 'X-Ctw-Internal': '1' },
    });
    State.serverCart = await res.json();
    recomputeMergedCart();
  }

  // Rebuild the merged cart view the wizard renders: the authoritative server
  // cart plus the local pending picks (the deferred-add queue) overlaid on top.
  // Pure recompute — never mutates State.serverCart — so it can run after any
  // server fetch OR any local queue change and the panel always shows
  // "what the cart WILL contain", which is what the customer expects to see.
  function recomputeMergedCart() {
    const base = State.serverCart || { items: [] };
    const items = (base.items || []).map(i => Object.assign({}, i));
    _addQueue.forEach(it => {
      const ex = items.find(i => i.id === it.id);
      if (ex) {
        const unit = ex.quantity ? (ex.final_line_price || ex.line_price || 0) / ex.quantity : 0;
        ex.quantity += it.quantity;
        ex.line_price = unit * ex.quantity;
        ex.final_line_price = unit * ex.quantity;
      } else {
        const product = PRODUCTS.find(p => p.variants.some(v => v.id === it.id));
        const variant = product && product.variants.find(v => v.id === it.id);
        if (!product || !variant) return;
        items.push({
          id: it.id,
          product_id: product.id,
          quantity: it.quantity,
          product_title: product.title,
          variant_title: variant.title,
          sku: variant.sku,
          image: variant.image || product.image,
          line_price: variant.price * it.quantity,
          final_line_price: variant.price * it.quantity,
        });
      }
    });
    State.cart = Object.assign({}, base, {
      items: items,
      total_price: items.reduce((s, i) => s + (i.final_line_price || i.line_price || 0), 0),
      item_count: items.reduce((s, i) => s + i.quantity, 0),
    });
  }

  // -------------------- Deferred add queue (local-first cart) --------------------
  // THE core design: while the customer is on the wizard page, "adding to cart"
  // is a purely LOCAL operation. Picks accumulate in _addQueue, render instantly
  // in the wizard panel (merged over the server cart), and persist to
  // localStorage. The real Shopify cart is only written at SYNC POINTS:
  //   1. clicking a cart / checkout link (flush, then navigate),
  //   2. clicking the header cart icon (flush, then open the drawer),
  //   3. leaving / refreshing the page (pagehide → one keepalive POST).
  // A whole wizard session therefore costs ONE batched /cart/add.js write —
  // which makes the Cloudflare cart-API rate limit (the 403/429 "managed
  // challenge" lockout) physically impossible to trip from adding products.
  let _addQueue = [];        // pending [{ id, quantity }]
  let _drainPromise = null;  // Promise of the running flush worker, or null
  let _inFlightBatch = null; // batch currently in a POST (for pagehide snapshot)
  const WRITE_GAP = 1500;    // ms between batches if a flush ever needs several

  // ---------- Pending-pick persistence (survives refresh / navigation) ----------
  // The queue is intentionally long-lived, so it ALWAYS lives in localStorage
  // too. On pagehide we mark a "flush attempt" with a snapshot of the server
  // cart's per-variant counts; on the next load we compare the fresh server
  // cart against that snapshot to tell which items actually landed (drop them)
  // and which didn't (keep them pending). That makes the unload-time keepalive
  // POST safe: it can neither lose items NOR double-add them.
  const PENDING_KEY = 'ctw_pending_adds_v1';
  const RESUME_NAV_KEY = 'ctw_resume_nav_v1';
  function persistPendingAdds() {
    try {
      if (_addQueue.length) {
        localStorage.setItem(PENDING_KEY, JSON.stringify({ v: 2, items: _addQueue, flushedAt: null }));
      } else {
        localStorage.removeItem(PENDING_KEY);
      }
    } catch (e) { /* storage unavailable — nothing we can do */ }
  }
  function serverVariantCounts() {
    const counts = {};
    ((State.serverCart && State.serverCart.items) || []).forEach(i => {
      counts[i.id] = (counts[i.id] || 0) + i.quantity;
    });
    return counts;
  }
  function markFlushAttempt(items) {
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify({
        v: 2, items: items, flushedAt: Date.now(), serverCounts: serverVariantCounts()
      }));
    } catch (e) { /* ignore */ }
  }
  function loadPersistedPending() {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      const arr = Array.isArray(data) ? data : (data && Array.isArray(data.items) ? data.items : null);
      if (!arr) { localStorage.removeItem(PENDING_KEY); return null; }
      const items = arr.filter(i => i && i.id && i.quantity > 0);
      if (!items.length) { localStorage.removeItem(PENDING_KEY); return null; }
      return {
        items: items,
        flushedAt: (data && !Array.isArray(data) && data.flushedAt) || null,
        serverCounts: (data && !Array.isArray(data) && data.serverCounts) || {},
      };
    } catch (e) { return null; }
  }
  function setResumeNav(href) {
    try {
      localStorage.setItem(RESUME_NAV_KEY, JSON.stringify({ href: href, t: Date.now() }));
    } catch (e) { /* ignore */ }
  }
  function takeResumeNav() {
    try {
      const raw = localStorage.getItem(RESUME_NAV_KEY);
      localStorage.removeItem(RESUME_NAV_KEY); // consume exactly once
      if (!raw) return null;
      const v = JSON.parse(raw);
      // Only honor a FRESH resume request (the reload happens within seconds).
      // A stale key — e.g. from an interrupted recovery days earlier — must
      // never hijack a later visit and bounce it to the cart.
      if (!v || !v.href || !v.t || (Date.now() - v.t) > 2 * 60 * 1000) return null;
      return v.href;
    } catch (e) { return null; }
  }

  // True while there are picks that have NOT yet been written to the server
  // (queued or mid-flush). Used to decide whether a sync point needs to flush
  // before letting the customer see the real cart.
  function cartWorkPending() {
    return _addQueue.length > 0 || _drainPromise !== null;
  }
  // True only while a flush POST is actually running — used to gate external
  // cart-event re-syncs (the queue itself is long-lived now, so it must NOT
  // block them the way it gated things in the old write-per-add model).
  function flushInProgress() {
    return _drainPromise !== null;
  }

  // Tell the theme to re-fetch and re-render its mini-cart / cart drawer from
  // the (now updated) server cart. This is what keeps the site's cart drawer in
  // sync with the wizard panel. It's a section-render GET, fired at most once
  // per coalesced flush, so it does not re-introduce the rate-limit problem.
  // When WE refresh the drawer, the theme responds by firing `cart:rerendered`.
  // We must NOT treat that as an external change and fire yet another /cart.js
  // reconcile — our own flow already reconciles. Mark a short suppression window
  // so the cart:rerendered listener ignores the echo of our own refresh.
  let _suppressRerenderUntil = 0;
  let _themeRefreshTimer = null;
  // Debounced so a burst of adds wakes the theme drawer / any cart-mirror app
  // (Experro et al.) ONCE instead of per-add. Each wake triggers an expensive
  // /cart?section_id render, which is the main remaining rate-limit pressure.
  function refreshThemeCartNow(immediate) {
    if (_cartLockedOut) return; // a section-render GET would only feed the 429 storm
    _suppressRerenderUntil = Date.now() + 2500;
    if (immediate) {
      // Drawer is about to be shown — re-render it right now, not on a delay.
      if (_themeRefreshTimer) { clearTimeout(_themeRefreshTimer); _themeRefreshTimer = null; }
      root.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { scrollToTop: false }
      }));
      return;
    }
    if (_themeRefreshTimer) return; // a refresh is already scheduled
    _themeRefreshTimer = setTimeout(() => {
      _themeRefreshTimer = null;
      if (_cartLockedOut) return;
      _suppressRerenderUntil = Date.now() + 2500;
      root.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { scrollToTop: false }
      }));
    }, 700);
  }

  // Adding is now a purely LOCAL operation: merge into the queue, persist,
  // re-render. No network, no timers — the server is only written at a sync
  // point (cart/checkout click, drawer open, or page leave).
  function enqueueAdd(items) {
    items.forEach(it => {
      const ex = _addQueue.find(q => q.id === it.id);
      if (ex) ex.quantity += it.quantity;
      else _addQueue.push({ id: it.id, quantity: it.quantity });
    });
    persistPendingAdds();
    recomputeMergedCart();
    renderCartPanel();
    renderAllSections(); // downstream sections may depend on form type, etc.
  }

  // Start the flush worker if it isn't already running. Returns the promise of
  // the running worker so sync points can await a fully-written cart.
  function startDrain(opts) {
    if (_cartLockedOut) { persistPendingAdds(); return Promise.resolve(); }
    if (_drainPromise) return _drainPromise; // already flushing — it will pick up new items
    if (!_addQueue.length) return Promise.resolve();
    _drainPromise = drainQueue(opts).finally(() => { _drainPromise = null; });
    return _drainPromise;
  }

  // Flush the queue ONE batch at a time (normally exactly one POST for the
  // whole wizard session), spaced if a flush ever needs several batches.
  // After the queue empties, refresh the theme drawer ONCE and reconcile ONCE
  // against the authoritative server cart.
  async function drainQueue(opts) {
    let lockedOut = false;
    let first = true;
    while (_addQueue.length) {
      if (_cartLockedOut) { persistPendingAdds(); lockedOut = true; break; }
      if (!first) await new Promise(r => setTimeout(r, WRITE_GAP));
      first = false;
      const batch = _addQueue;
      _addQueue = [];
      _inFlightBatch = batch;
      try {
        await addItemsToCart(batch, opts);
        _inFlightBatch = null;
        persistPendingAdds(); // queue shrank — keep the snapshot accurate (clears when empty)
      } catch (err) {
        _inFlightBatch = null;
        console.error('[CTW] batched flush failed', err);
        handleCartError(err);
        if (err && err.code === 'CHALLENGE') {
          // Locked out by the Cloudflare challenge mid-batch. Put the
          // unconfirmed batch BACK in the queue and persist it — picks stay
          // local and the next sync point (after the refresh that solves the
          // challenge) writes them. Nothing is ever silently dropped.
          _addQueue = batch.concat(_addQueue);
          persistPendingAdds();
          lockedOut = true;
          break;
        }
        // Genuine failure (bad variant, out of stock...): abandon the rest of
        // the queue and fall through to the reconcile below, which pulls
        // authoritative server state and drops any phantom items.
        _addQueue = [];
        persistPendingAdds();
        break;
      }
    }
    if (lockedOut) return;
    // Queue fully flushed — ONE drawer re-render for the whole run, then
    // reconcile the panel against the real cart (applies discounts, fixes
    // ordering, removes phantoms).
    refreshThemeCartNow();
    try {
      await fetchCart();
      renderCartPanel();
      renderAllSections();
    } catch (e) { /* non-fatal */ }
  }

  // Drain the queue and wait for the worker so the server cart is fully up to
  // date. Resolves even on error so navigation is never permanently blocked.
  async function settleCartAdds() {
    try { await startDrain(); } catch (e) { /* ignore */ }
    if (_drainPromise) { try { await _drainPromise; } catch (e) { /* ignore */ } }
  }

  // Discard any pending adds (used by Start Over so a late flush can't
  // re-populate a cart we just cleared). The in-flight worker, if any, is
  // awaited separately by the caller.
  function cancelPendingAdds() {
    _addQueue = [];
    persistPendingAdds(); // queue is now empty — clear any persisted snapshot too
  }

  // -------------------- Sync points --------------------
  // The deferred queue is written to the real cart at exactly these moments:
  //   pagehide        → one keepalive POST (covers refresh, back, any link out)
  //   cart drawer     → flush, then open the drawer showing the true cart
  //   cart / checkout → flush, then navigate
  function bindFlushBeforeUnload() {
    // Leaving the page (refresh, navigation, close). Mark the flush attempt
    // FIRST — with a snapshot of the server cart's per-variant counts — then
    // fire the keepalive POST. On the next load, init() compares the fresh
    // server cart against the snapshot: items that landed are dropped, items
    // that didn't stay pending. Safe against both loss AND double-adds.
    window.addEventListener('pagehide', () => {
      const combined = (_inFlightBatch || []).concat(_addQueue);
      if (!combined.length) return;
      markFlushAttempt(combined);
      if (!_drainPromise && !_cartLockedOut) startDrain({ keepalive: true });
    });

    const CART_NAV = /\/(cart|checkout)(\/|\?|#|$)/i;
    // Persist pending picks + remember the destination, then reload. The reload
    // (a top-level navigation) solves the Cloudflare challenge; init() restores
    // the picks, flushes them, and continues to the destination — the customer
    // still ends up where they clicked, WITH all their items.
    function recoverThenNavigate(href) {
      persistPendingAdds();
      setResumeNav(href || '/checkout');
      hardReload();
    }
    // Re-dispatch marker so our own programmatic click on the drawer toggle
    // isn't intercepted again.
    let _passThroughClick = false;
    // Capture phase so we run before the theme's own cart / checkout handlers.
    document.addEventListener('click', (e) => {
      if (_passThroughClick) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // Header cart icon: the theme turns this <a href="/cart"> into a drawer
      // toggle. Sync first so the drawer always shows the TRUE cart, then let
      // the theme open it. This is the one place mid-wizard where the customer
      // can see the system cart — and now it's always accurate there.
      const toggle = e.target.closest('[data-action="toggle-mini-cart"]');
      if (toggle) {
        if (!cartWorkPending()) return; // already in sync — open normally
        e.preventDefault();
        e.stopPropagation();
        settleCartAdds().then(() => {
          if (_cartLockedOut && _addQueue.length) return; // banner is up; don't show a stale drawer
          refreshThemeCartNow(true); // immediate re-render with the fresh cart
          _passThroughClick = true;
          try { toggle.click(); } finally { _passThroughClick = false; }
        });
        return;
      }

      const a = e.target.closest('a[href]');
      const checkoutBtn = e.target.closest('button[name="checkout"], [data-action="checkout"]');
      let href = null;
      if (a && CART_NAV.test(a.getAttribute('href') || '')) href = a.href;
      if (!href && !checkoutBtn) return;
      if (_cartLockedOut) {
        if (_addQueue.length) {
          // Going to the cart now would show a cart MISSING what the customer
          // sees in the wizard panel — the exact confusion we're fixing.
          e.preventDefault();
          e.stopPropagation();
          recoverThenNavigate(href);
        }
        // Nothing pending: let the navigation proceed — being a top-level
        // navigation, it solves the challenge by itself.
        return;
      }
      if (!cartWorkPending()) return; // server already in sync — let it proceed normally
      e.preventDefault();
      e.stopPropagation();
      settleCartAdds().finally(() => {
        if (_cartLockedOut && _addQueue.length) {
          // The flush itself hit the challenge — recover via reload.
          recoverThenNavigate(href);
          return;
        }
        window.location.href = href || '/checkout';
      });
    }, true);
  }

  // -------------------- Rate-limit recovery banner --------------------
  // When Shopify's Cloudflare challenge locks the cart API, fetch can't recover
  // in-page. A full top-level navigation (refresh) solves the challenge and
  // restores cart access. The customer's answers persist in localStorage and the
  // cart reloads from the server, so nothing is lost.
  let _challengeBanner = null;
  function handleCartError(err) {
    if (err && err.code === 'CHALLENGE') showRateLimitBanner();
  }
  // Reload that cannot be swallowed: reload() first, and if anything on the
  // page interfered (SPA routers etc.), force a same-URL navigation as backup.
  function hardReload() {
    try { window.location.reload(); } catch (e) { /* fall through */ }
    setTimeout(() => {
      try { window.location.href = window.location.pathname + window.location.search; } catch (e) { /* ignore */ }
    }, 350);
  }
  function showRateLimitBanner() {
    if (_challengeBanner) {
      // Already visible — pulse it so a blocked action visibly points here.
      _challengeBanner.classList.remove('is-pulse');
      void _challengeBanner.offsetWidth; // restart the CSS animation
      _challengeBanner.classList.add('is-pulse');
      return;
    }
    const bar = document.createElement('div');
    bar.className = 'ctw__ratelimit';
    // data-ctw-reload: also handled by a FIRST-registered capture listener in
    // the head guard (snippets/ctw-cart-guard.liquid), so no third-party click
    // handler can stopPropagation before the reload fires.
    bar.innerHTML =
      '<span>Cart updates are paused by a temporary traffic limit. Everything you’ve added is saved — refresh to apply it to your cart.</span>' +
      '<button type="button" class="ctw__ratelimit-refresh" data-ctw-reload>Refresh</button>';
    bar.querySelector('.ctw__ratelimit-refresh').addEventListener('click', hardReload);
    document.body.appendChild(bar);
    _challengeBanner = bar;
  }

  // -------------------- Derived helpers (read cart for context) --------------------
  // Detect which form-type the customer has added (by inspecting cart items + product tags).
  // Returns one of: 'euro', 'commercial', 'standard', or null if none yet.
  function detectFormTypeInCart() {
    const ids = (State.cart.items || []).map(i => i.product_id);
    const tagsOfCartProducts = [];
    ids.forEach(id => {
      const p = PRODUCTS.find(pp => pp.id === id);
      if (p) tagsOfCartProducts.push(...(p.tags || []));
    });
    if (tagsOfCartProducts.indexOf('wizard:form-euro') !== -1) return 'euro';
    if (tagsOfCartProducts.indexOf('wizard:form-commercial') !== -1) return 'commercial';
    if (tagsOfCartProducts.indexOf('wizard:form-standard') !== -1) return 'standard';
    return null;
  }

  // Count "Full Package" and "Half Package" form kits in cart, summed across all form products.
  function countFormKitsInCart() {
    let full = 0, half = 0;
    (State.cart.items || []).forEach(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      if (!p || !p.tags) return;
      const isForm = p.tags.indexOf('wizard:forms') !== -1;
      if (!isForm) return;
      const vTitle = (item.variant_title || '').toLowerCase();
      if (/full/.test(vTitle)) full += item.quantity;
      else if (/half/.test(vTitle)) half += item.quantity;
      else full += item.quantity; // single-variant form products are treated as full
    });
    return { full, half };
  }

  // Count Z Clips boxes in cart.
  function countClipBoxesInCart() {
    let n = 0;
    (State.cart.items || []).forEach(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      if (p && p.tags && p.tags.indexOf('wizard:clip') !== -1) n += item.quantity;
    });
    return n;
  }

  // Count bags of concrete mix in cart (1 White bag, 1 Liqui-Crete single = 1 bag, 1 Liqui-Crete 6-pack = 6 bags).
  function countMixBagsInCart() {
    let n = 0;
    (State.cart.items || []).forEach(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      if (!p || !p.tags) return;
      if (p.tags.indexOf('wizard:mix-liquicrete-6pack') !== -1) n += item.quantity * 6;
      else if (p.tags.indexOf('wizard:mix') !== -1) n += item.quantity;
    });
    return n;
  }

  // Detect which mix type(s) are in cart — used by Tools section to pick which finishing tools to show.
  // Returns { white: <bags>, liquicrete: <bags> }
  function detectMixTypesInCart() {
    const out = { white: 0, liquicrete: 0 };
    (State.cart.items || []).forEach(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      if (!p || !p.tags) return;
      if (p.tags.indexOf('wizard:mix-white') !== -1) out.white += item.quantity;
      else if (p.tags.indexOf('wizard:mix-liquicrete-6pack') !== -1) out.liquicrete += item.quantity * 6;
      else if (p.tags.indexOf('wizard:mix-liquicrete') !== -1) out.liquicrete += item.quantity;
    });
    return out;
  }

  // "White-on-white" build: White Countertop Mix is in the cart, no Liqui-Crete,
  // and the ONLY colorant (if any) is the Bright White Color-Pack. Used to hide
  // the gray-concrete finishing tools (Magnesium Float + both Stainless trowels),
  // which are only for Liqui-Crete / tinted pours.
  function isWhiteOnlyBuild() {
    const types = detectMixTypesInCart();
    if (types.white <= 0 || types.liquicrete > 0) return false;
    const colorItems = (State.cart.items || []).filter(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      return p && p.tags && p.tags.indexOf('wizard:coloring') !== -1;
    });
    // No colorant, or only Bright White Color-Pack.
    return colorItems.every(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      return p && p.handle === 'color-pack' && (item.variant_title || '').trim().toLowerCase() === 'bright white';
    });
  }

  // Form liners only work with Square Edge form variants. If the customer has
  // a different edge profile in their cart, we warn before letting them add
  // an incompatible form-liner combo.
  const FORM_LINER_COMPATIBLE_SKUS = new Set([
    'SE-001-S', 'SE-002-S',         // Standard Square Edge (full / half)
    'ESE-001-S', 'ESE-002-S',       // EuroForm Square Edge (full / half)
    'SEC-003-S',                    // Commercial Bar Form Package
  ]);
  function hasIncompatibleFormInCart() {
    return (State.cart.items || []).some(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      if (!p || !p.tags) return false;
      if (p.tags.indexOf('wizard:forms') === -1) return false;
      return !FORM_LINER_COMPATIBLE_SKUS.has(item.sku);
    });
  }

  // The Z Edge Expert Set only works with Standard Square Edge form profiles.
  // These helpers gate its visibility (hidden when a EuroForm or Commercial
  // form is in the cart) and the add-time warning.
  const EDGE_EXPERT_HANDLE = 'z-edge-expert-set';
  // Commercial Bar Form Package — a 60 ft Full-only form (no Half variant) that
  // covers the front edge only, NOT the backwall. Used for its combo button +
  // the "no backwall form" card warning.
  const COMMERCIAL_FORM_HANDLE = 'commercial-bar-form-package-large-square-edge';
  // Bendable Square — a single bendable form (one "Default Title" variant, not
  // package-scaled), so it's excluded from the form qty auto-fill.
  const BENDABLE_SQUARE_HANDLE = 'bendable-square-form';
  // The three plain square-edge form profiles. The Edge Expert Set is RECOMMENDED
  // for any OTHER form profile (bullnose, ogee, fancy radius, modern, etc.) —
  // those benefit from the edge tool; a basic square edge doesn't.
  const SQUARE_EDGE_FORM_HANDLES = new Set([
    'square-edge',                                   // Square Edge
    'euroform-square-edge',                          // EuroForm
    'commercial-bar-form-package-large-square-edge', // Commercial Square
    'bendable-square-form',                          // Bendable Square (also a square profile)
  ]);
  function hasNonSquareEdgeFormInCart() {
    return (State.cart.items || []).some(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      if (!p || !p.tags) return false;
      if (p.tags.indexOf('wizard:forms') === -1) return false;
      return !SQUARE_EDGE_FORM_HANDLES.has(p.handle);
    });
  }
  function hasEdgeIncompatibleFormInCart() {
    return (State.cart.items || []).some(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      if (!p || !p.tags) return false;
      return p.tags.indexOf('wizard:form-euro') !== -1 || p.tags.indexOf('wizard:form-commercial') !== -1;
    });
  }
  function hasEdgeExpertInCart() {
    return (State.cart.items || []).some(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      return p && p.handle === EDGE_EXPERT_HANDLE;
    });
  }

  // Variants visible for a product given current answers. Currently only the
  // Color-Pack hides its "Black" swatch outdoors (Black is the one Color-Pack
  // color that can't be used outdoors).
  function visibleVariantsFor(product) {
    let vs = product.variants || [];
    if (product.handle === 'color-pack' && State.answers.useLocation === 'outdoor') {
      vs = vs.filter(v => (v.title || '').trim().toLowerCase() !== 'black');
    }
    return vs;
  }

  // Mix exclusivity: White Countertop Mix and Liqui-Crete should not be in the
  // cart together. Helper returns which mix types are present.
  function getMixesInCart() {
    let hasWhite = false, hasLC = false;
    (State.cart.items || []).forEach(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      if (!p || !p.tags) return;
      if (p.tags.indexOf('wizard:mix-white') !== -1) hasWhite = true;
      if (p.tags.indexOf('wizard:mix-liquicrete') !== -1) hasLC = true;
    });
    return { hasWhite, hasLC };
  }

  // Sealer compatibility: Counter-Shine only works on top of SiAcryl 14.
  // Helper categorises sealers already in cart.
  function getSealersInCart() {
    let siacryl = false, countershine = false, other = false;
    (State.cart.items || []).forEach(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      if (!p || !p.tags) return;
      if (p.tags.indexOf('wizard:sealers') === -1) return;
      if (p.handle === 'z-siacryl-14-sealer') siacryl = true;
      else if (p.tags.indexOf('wizard:sealer-countershine') !== -1) countershine = true;
      else other = true;
    });
    return { siacryl, countershine, other };
  }

  // Inspect cart for form-liner woodgrain vs. non-woodgrain choices (drives texture-mat filtering).
  // Returns 'woodgrain', 'other', 'both', or null.
  function detectFormLinerInCart() {
    let hasWood = false, hasOther = false;
    (State.cart.items || []).forEach(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      if (!p || !p.tags) return;
      if (p.tags.indexOf('wizard:formliner') === -1) return;
      if (p.tags.indexOf('wizard:formliner-woodgrain') !== -1) hasWood = true;
      else hasOther = true;
    });
    if (hasWood && hasOther) return 'both';
    if (hasWood) return 'woodgrain';
    if (hasOther) return 'other';
    return null;
  }

  // True if the cart (merged: server + pending picks) contains a texture mat —
  // i.e. the customer is stamping the top, which is incompatible with wet
  // polishing. Drives both the sanding-section filter and the add warning.
  function hasTextureMatInCart() {
    return (State.cart.items || []).some(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      return p && p.tags && p.tags.indexOf('wizard:texturemat') !== -1;
    });
  }

  // True if the cart contains a wet-polishing product (the Wet Polisher, the
  // 3 Head Polisher, or the Diamond Polishing Pads).
  function hasWetPolishProductInCart() {
    return (State.cart.items || []).some(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      return p && p.tags && (p.tags.indexOf('wizard:sand-wetpolisher') !== -1 || p.tags.indexOf('wizard:sand-3headpolisher') !== -1 || p.tags.indexOf('wizard:sand-polishpads') !== -1);
    });
  }

  // Projects over this many sq ft get the 3 Head Polisher recommended instead
  // of the single-head Wet Polisher (no sq ft entered = Wet Polisher). Falls
  // back to the Wet Polisher if the 3 Head is missing from the data or sold out,
  // so exactly one polisher is always badged.
  const THREE_HEAD_POLISHER_MIN_SQFT = 50;
  function prefersThreeHeadPolisher() {
    const threeHead = findByTagSingle('wizard:sand-3headpolisher');
    return !!threeHead && !!threeHead.available && num(State.answers.sqFt) > THREE_HEAD_POLISHER_MIN_SQFT;
  }

  function cartHasTag(tag) {
    return (State.cart.items || []).some(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      return p && p.tags && p.tags.indexOf(tag) !== -1;
    });
  }

  // Which polisher to size the Diamond Polishing Pads for: the one already in
  // the cart (3 Head wins if both are), otherwise the recommended one. The
  // 3 Head Polisher needs 3 packs (one 50–3000 grit set per head).
  function usesThreeHeadPolisher() {
    if (cartHasTag('wizard:sand-3headpolisher')) return true;
    if (cartHasTag('wizard:sand-wetpolisher')) return false;
    return prefersThreeHeadPolisher();
  }

  // Translate cart form type to the form-liner variant size label used in product variant titles.
  // Returns a regex pattern (case-insensitive) or null when no form type set.
  function formLinerVariantPattern() {
    const formType = detectFormTypeInCart();
    if (formType === 'euro') return /euroform|1-?1\/4/i;
    if (formType === 'commercial') return /commercial|3-?1\/2/i;
    if (formType === 'standard') return /standard|2-?1\/4/i;
    return null;
  }

  // Does this form-liner product have a variant compatible with the chosen form type?
  function formLinerHasMatchingVariant(product) {
    const pattern = formLinerVariantPattern();
    if (!pattern) return true; // no form type chosen yet → show everything
    if (!product.variants || !product.variants.length) return false;
    return product.variants.some(v => pattern.test(v.title || ''));
  }

  // -------------------- Section product selection (which products to show) --------------------
  function productsForSection(section) {
    const cartFormType = detectFormTypeInCart();
    switch (section) {
      case 'forms':
        return byTag('wizard:forms');

      case 'mix': {
        const all = byTag('wizard:mix');
        // If Euroforms are in cart, only show White Mix (Liqui-Crete is not compatible).
        if (cartFormType === 'euro') return all.filter(p => p.tags.indexOf('wizard:mix-white') !== -1);
        return all;
      }

      case 'accessories': {
        let list = byTag('wizard:accessories');
        // Hide sink form unless sink=yes
        if (State.answers.sink !== 'yes') list = list.filter(p => p.tags.indexOf('wizard:sinkform') === -1);
        // Faucet knockouts: only the type matching the form type. Hide both if commercial (neither works).
        if (cartFormType === 'commercial') {
          list = list.filter(p => p.tags.indexOf('wizard:faucet-knockout-standard') === -1 && p.tags.indexOf('wizard:faucet-knockout-euro') === -1);
        } else if (cartFormType === 'euro') {
          list = list.filter(p => p.tags.indexOf('wizard:faucet-knockout-standard') === -1);
        } else if (cartFormType === 'standard') {
          list = list.filter(p => p.tags.indexOf('wizard:faucet-knockout-euro') === -1);
        }
        // Also: if sink=no, hide faucet knockouts entirely
        if (State.answers.sink !== 'yes') {
          list = list.filter(p => p.tags.indexOf('wizard:faucet-knockout-standard') === -1 && p.tags.indexOf('wizard:faucet-knockout-euro') === -1);
        }
        // Edge Expert Set: Standard form profiles only — hide it when a EuroForm
        // or Commercial form is in the cart.
        if (hasEdgeIncompatibleFormInCart()) {
          list = list.filter(p => p.handle !== EDGE_EXPERT_HANDLE);
        }
        return list;
      }

      case 'sealers': {
        // Outdoor + High-Gloss: no recommended combination — show nothing.
        if (State.answers.useLocation === 'outdoor' && State.answers.sealerFinish === 'highgloss') {
          return [];
        }
        let list = byTag('wizard:sealers');
        if (State.answers.useLocation === 'indoor') {
          list = list.filter(p => p.tags.indexOf('wizard:sealer-indoor') !== -1);
        } else if (State.answers.useLocation === 'outdoor') {
          // Only outdoor-rated sealers (this is what excludes Aqua-Poxy, which
          // is indoor-only, from ever appearing for an outdoor project). Combined
          // with the finish filter below this yields exactly:
          //   outdoor + satin      → SiAcryl 14 + Aqua-Thane M35
          //   outdoor + semi-gloss → SiAcryl 14 + Counter-Shine
          //   outdoor + high-gloss → none (handled by the early return above)
          // The Recommended badge lands on SiAcryl 14 (see recommendedQtyFor).
          list = list.filter(p => p.tags.indexOf('wizard:sealer-outdoor') !== -1);
        }
        if (State.answers.sealerFinish === 'satin') {
          list = list.filter(p => p.tags.indexOf('wizard:sealer-satin') !== -1);
        } else if (State.answers.sealerFinish === 'semigloss') {
          list = list.filter(p => p.tags.indexOf('wizard:sealer-semigloss') !== -1);
        } else if (State.answers.sealerFinish === 'highgloss') {
          list = list.filter(p => p.tags.indexOf('wizard:sealer-highgloss') !== -1);
        }
        return list;
      }

      case 'texturing': {
        let list = byTag('wizard:texturing');
        const wantEdge = State.answers.textureEdge === 'yes';
        const wantTop = State.answers.textureTop === 'yes';
        list = list.filter(p => {
          const tags = p.tags || [];
          const isFormLiner = tags.indexOf('wizard:formliner') !== -1;
          const isTextureMat = tags.indexOf('wizard:texturemat') !== -1;
          const isMountingTape = tags.indexOf('wizard:mounting-tape') !== -1;
          const isRelease = tags.indexOf('wizard:release') !== -1;

          // Form liners: only shown if user wants textured edge AND product has a variant for the form type
          if (isFormLiner) return wantEdge && formLinerHasMatchingVariant(p);
          // Mounting tape: only shown if textured edge is yes
          if (isMountingTape) return wantEdge;
          // Z Release: only shown if textured top is yes
          if (isRelease) return wantTop;
          // Texture mats: only if textured top is yes, plus woodgrain rule
          if (isTextureMat) {
            if (!wantTop) return false;
            const liner = detectFormLinerInCart();
            const isWood = tags.indexOf('wizard:texturemat-woodgrain') !== -1;
            if (liner === 'woodgrain') return isWood;        // only the woodgrain mat
            if (liner === 'other' || liner === 'both') return !isWood; // hide woodgrain mat
            return true; // no form liner in cart yet → show everything
          }
          return false;
        });
        return list;
      }

      case 'sanding': {
        const all = byTag('wizard:sanding'); // includes out-of-stock items
        const isGemPad      = p => p.tags.indexOf('wizard:sand-gempad') !== -1;
        const isGritDisc    = p => p.tags.indexOf('wizard:sand-gritdisc') !== -1;
        const isWetPolisher = p => p.tags.indexOf('wizard:sand-wetpolisher') !== -1;
        const isThreeHead   = p => p.tags.indexOf('wizard:sand-3headpolisher') !== -1;
        const isPolishPads  = p => p.tags.indexOf('wizard:sand-polishpads') !== -1;
        const wantsWetPolish = State.answers.wetPolish === 'yes';

        // Stamped top (texture mat in cart): Gem-Pad + Grit-Discs are the
        // relevant items. If the customer still chose to wet polish, also show
        // the polishers + Diamond Polishing Pads (shown, but NOT recommended
        // — see recommendedQtyFor — and the add warning fires).
        if (hasTextureMatInCart()) {
          return all.filter(p =>
            isGemPad(p) || isGritDisc(p) ||
            (wantsWetPolish && (isWetPolisher(p) || isThreeHead(p) || isPolishPads(p)))
          );
        }
        // Wet polishing chosen (no texture mat): only the Wet Polisher, 3 Head
        // Polisher, Diamond Polishing Pads, and Gem-Pad.
        if (wantsWetPolish) {
          return all.filter(p => isWetPolisher(p) || isThreeHead(p) || isPolishPads(p) || isGemPad(p));
        }
        // Default: every sanding product.
        return all;
      }
      case 'brackets': {
        if (State.answers.needBrackets !== 'yes') return [];
        return byTag('wizard:brackets');
      }
      case 'grills': {
        if (State.answers.needGrill !== 'yes') return [];
        return byTag('wizard:grills');
      }

      case 'tools': {
        let list = byTag('wizard:tools');
        // "No" to finishing tools: hide the finishing tools and show only the
        // basics — Trowel-Slick, Vibra-Blade, and both paddle mixers
        // (Vibra-Blade + mixers carry wizard:tool-always).
        if (State.answers.needFinishingTools === 'no') {
          list = list.filter(p =>
            p.tags.indexOf('wizard:tool-trowelslick') !== -1 ||
            p.tags.indexOf('wizard:tool-always') !== -1
          );
        }
        // White-on-white build: hide the gray-concrete finishing tools
        // (Magnesium Float + both Stainless trowels) — only for Liqui-Crete/tint.
        if (isWhiteOnlyBuild()) {
          list = list.filter(p =>
            p.tags.indexOf('wizard:tool-magfloat') === -1 &&
            p.tags.indexOf('wizard:tool-stainless') === -1
          );
        }
        return list;
      }

      case 'coloring': {
        let list = byTag('wizard:coloring');
        // Outdoor: hide Aqua-Tint dye (indoor only)
        if (State.answers.useLocation === 'outdoor') {
          list = list.filter(p => p.tags.indexOf('wizard:color-aquatint') === -1);
        }
        // Filter by desired look
        const style = State.answers.colorStyle;
        if (style === 'solid') {
          list = list.filter(p =>
            p.tags.indexOf('wizard:color-colorpack') !== -1 ||
            p.tags.indexOf('wizard:color-terratint') !== -1);
        } else if (style === 'mottled') {
          list = list.filter(p => p.tags.indexOf('wizard:color-aquastain') !== -1);
        } else if (style === 'deep') {
          list = list.filter(p => p.tags.indexOf('wizard:color-aquatint') !== -1);
        }
        return list;
      }
    }
    return [];
  }

  // -------------------- Recommendation math --------------------
  function recommendForms() {
    const profile = num(State.answers.profileFt);
    const backwall = num(State.answers.backwallFt);
    if (profile <= 0 && backwall <= 0) return null;
    const needed = Math.max(profile, backwall);

    // Apply the exact rules:
    //   ≤30 ft → 1 Half Package
    //   31–60 ft → 1 Full Package
    //   >60 ft → combination
    let full, half;
    if (needed <= 30) { full = 0; half = 1; }
    else if (needed <= 60) { full = 1; half = 0; }
    else {
      full = Math.floor(needed / 60);
      const remainder = needed - full * 60;
      if (remainder === 0) half = 0;
      else if (remainder <= 30) half = 1;
      else { full += 1; half = 0; }
    }
    const capacity = full * COVERAGE.fullPackageFt + half * COVERAGE.halfPackageFt;

    const parts = [];
    if (full > 0) parts.push(`<strong>${full} Full Package${full > 1 ? 's' : ''}</strong>`);
    if (half > 0) parts.push(`<strong>${half} Half Package</strong>`);

    // 5-ft buffer warning per side
    const warnings = [];
    function sideWarning(label, value) {
      if (value <= 0) return;
      if (capacity - value > 5) return;
      // Suggest the next-step-up package
      const suggestion = half > 0
        ? `Consider upgrading the Half Package to a Full Package for ${value > 30 ? capacity + 30 : 60} ft of coverage`
        : `Consider adding a Half Package for ${capacity + 30} ft of coverage`;
      warnings.push(`<strong>${value} ft</strong> of ${label} is cutting it close to the recommended capacity. ${suggestion} so you do not run short.`);
    }
    sideWarning('profile (edge) form', profile);
    sideWarning('backwall form', backwall);

    let html = `Based on <strong>${profile} ft</strong> of profile and <strong>${backwall} ft</strong> of backwall, you'll need approximately ${parts.join(' + ')}. This covers up to <strong>${capacity} ft</strong> on each side. Pick your edge style(s) and matching package size(s) below.`;
    if (warnings.length) {
      html += '<div class="ctw-warn">' + warnings.map(w => '⚠️ ' + w).join('<br>') + '</div>';
    }
    return { html, full, half, capacity };
  }

  // "2 Full Packages + 1 Half Package" — list of human-readable package counts.
  function packPartsText(full, half) {
    const parts = [];
    if (full > 0) parts.push(full + ' Full Package' + (full > 1 ? 's' : ''));
    if (half > 0) parts.push(half + ' Half Package' + (half > 1 ? 's' : ''));
    return parts;
  }

  // Can this variant supply `qty` units? True for untracked / backorder-allowed
  // variants; for inventory-tracked ones, only when enough is in stock.
  function variantCanSupply(v, qty) {
    if (!v) return false;
    if (v.inventoryManagement === 'shopify' && v.inventoryPolicy !== 'continue') {
      return (v.inventoryQuantity || 0) >= qty;
    }
    return true;
  }

  // When a form's recommendation is a Full + Half COMBINATION of the same
  // product (profile/backwall in 61–90 or 121–150 ft → "1 Full + 1 Half" /
  // "2 Full + 1 Half"), the card's single dropdown + qty can only add one
  // variant per click, so a customer can miss the Half. This resolves that combo
  // so the card/quick-view can offer a one-click "add both lines" button (via
  // enqueueAdd's multi-line path). Returns null for everything else (single
  // package, exact multiples, non-form products, or a missing/sold-out Full or
  // Half variant) so those cards stay exactly as they are. Variant matching uses
  // the same /full/i and /half/i title test as defaultVariantFor, so the lines
  // enqueued always agree with the recommended-qty prefill and the badge label.
  function formComboPlan(product) {
    if (!product || !product.tags) return null;
    if (product.tags.indexOf('wizard:forms') === -1) return null;
    const r = recommendForms();
    if (!r) return null;
    // ONLY for a true Full + Half combination (two variants the single dropdown
    // can't add in one click). A full-only form (e.g. the Commercial Bar Form,
    // single variant) gets NO button — its qty box is auto-filled instead (see
    // commercialFormQty in renderProductCard).
    const fullCount = r.full, halfCount = r.half;
    if (!(fullCount > 0 && halfCount > 0)) return null;
    const vs = product.variants || [];
    const fullVariant = vs.find(v => /full/i.test(v.title || '') && v.available);
    const halfVariant = vs.find(v => /half/i.test(v.title || '') && v.available);
    if (!fullVariant || !halfVariant) return null;
    // Don't offer a one-click combo that would exceed tracked stock — the combo
    // bypasses the qty input, and an over-qty batched flush is rejected wholesale.
    // Degrade to the manual dropdown (which surfaces a max=) instead.
    if (!variantCanSupply(fullVariant, fullCount) || !variantCanSupply(halfVariant, halfCount)) return null;
    return {
      fullCount, halfCount, fullVariant, halfVariant,
      text: packPartsText(fullCount, halfCount).join(' + '),
    };
  }

  // Auto-fill quantity for a form card/quick-view when a SINGLE package variant
  // is recommended (e.g. 98 ft → 2 Full Packages). Returns the count, or null
  // when there's nothing to auto-fill. A Full+Half combo is NOT auto-filled here
  // — its one-click combo button handles both lines. Excludes Bendable Square
  // (a single bendable form, not package-scaled). The Commercial Bar Form is
  // front-edge only (no backwall), so it sizes off the profile footage alone.
  function formRecQty(product, section) {
    if (section !== 'forms' || !product || !product.tags) return null;
    if (product.tags.indexOf('wizard:forms') === -1) return null;
    if (product.handle === BENDABLE_SQUARE_HANDLE) return null;
    if (product.handle === COMMERCIAL_FORM_HANDLE) {
      const profileFt = num(State.answers.profileFt);
      return profileFt > 0 ? Math.ceil(profileFt / COVERAGE.fullPackageFt) : null;
    }
    const r = recommendForms();
    if (!r) return null;
    if (r.full > 0 && r.half === 0) return r.full; // pre-selected variant is Full
    if (r.half > 0 && r.full === 0) return r.half; // pre-selected variant is Half
    return null; // Full + Half combo → handled by the combo button
  }

  function recommendMix() {
    const sqFt = num(State.answers.sqFt);
    if (sqFt <= 0) return null;
    const formType = detectFormTypeInCart() || 'standard';
    const formTypeLabel = formType === 'euro' ? 'EuroForms' : formType === 'commercial' ? 'Commercial Forms' : 'Standard Forms';
    const whiteBags = Math.ceil(sqFt / COVERAGE.whiteMixSqftPerBag[formType]);
    let lc = null;
    if (formType !== 'euro') {
      const lcBags = Math.ceil(sqFt / COVERAGE.liquiCreteSqftPerBag[formType]);
      lc = { bags: lcBags };
    }
    let html = `Based on <strong>${sqFt} sq ft</strong> and <strong>${formTypeLabel}</strong>:<br>`;
    html += `• <strong>White Countertop Mix:</strong> ~${whiteBags} bag${whiteBags > 1 ? 's' : ''}<br>`;
    if (lc) {
      const sixPacks = Math.floor(lc.bags / 6);
      const singles = lc.bags % 6;
      const lcBreakdown = sixPacks > 0
        ? `${sixPacks} × 6-Pack${singles > 0 ? ` + ${singles} × Single` : ''} (saves money)`
        : `${singles} × Single`;
      html += `• <strong>Z Liqui-Crete:</strong> ~${lc.bags} bag${lc.bags > 1 ? 's' : ''} → ${lcBreakdown}`;
    } else {
      html += `• <em>Liqui-Crete is not compatible with EuroForms.</em>`;
    }
    return { html, whiteBags, lcBags: lc ? lc.bags : 0, formType };
  }

  function recommendAccessories() {
    const sqFt = num(State.answers.sqFt);
    if (sqFt <= 0 && State.answers.sink !== 'yes') return null;
    const meshRolls = sqFt > 0 ? Math.ceil(sqFt / COVERAGE.meshSqftPerRoll) : 0;
    const clipBoxes = sqFt > 0 ? Math.ceil(sqFt / COVERAGE.clipsSqftPerBox) : 0;
    const kits = countFormKitsInCart();
    const clipsInCart = countClipBoxesInCart();
    const screwsNeeded = (kits.full * COVERAGE.screwsPerFullPackage) +
                        (kits.half * COVERAGE.screwsPerHalfPackage) +
                        ((clipsInCart || clipBoxes) * COVERAGE.screwsPerClipBox);
    const screwBoxes = screwsNeeded > 0 ? Math.ceil(screwsNeeded / COVERAGE.screwsPerBox) : 0;

    const lines = [];
    if (meshRolls > 0) lines.push(`• <strong>FG50 Reinforcement Mesh:</strong> ${meshRolls} roll${meshRolls > 1 ? 's' : ''} (covers ${meshRolls * 100} sq ft)`);
    if (clipBoxes > 0) lines.push(`• <strong>Z Clips:</strong> ${clipBoxes} box${clipBoxes > 1 ? 'es' : ''}`);
    if (State.answers.sink === 'yes') {
      lines.push(`• <strong>Sink Form:</strong> 1`);
      if (num(State.answers.faucetHoles) > 0) lines.push(`• <strong>Faucet Knockouts:</strong> ${num(State.answers.faucetHoles)} (one per faucet hole)`);
    }
    if (screwBoxes > 0) lines.push(`• <strong>Construction Screws:</strong> ${screwBoxes} box${screwBoxes > 1 ? 'es' : ''} (~${screwsNeeded} screws needed)`);

    // Warning: Commercial Forms + undermount sink — faucet knockouts don't work
    let html = lines.join('<br>');
    if (detectFormTypeInCart() === 'commercial' && State.answers.sink === 'yes') {
      html += '<div class="ctw-warn">⚠️ <strong>Faucet Knockouts are not compatible with Commercial Forms.</strong> Use a piece of PVC pipe (cut to the thickness of your countertop) in place of a knockout to create each faucet hole.</div>';
    }
    if (!lines.length && !html) return null;
    return { html, meshRolls, clipBoxes, screwBoxes };
  }

  function recommendSealers() {
    const loc = State.answers.useLocation;
    const finish = State.answers.sealerFinish;
    // Outdoor + High-Gloss: nothing suitable to offer — explain why the
    // section is empty instead of leaving a bare "no products" message.
    if (loc === 'outdoor' && finish === 'highgloss') {
      return { html: '<div class="ctw-warn">⚠️ We don’t recommend a high-gloss finish for outdoor concrete, so no sealers are shown. For outdoor projects choose <strong>Satin</strong> or <strong>Semi-Gloss</strong> — <strong>SiAcryl 14</strong> is our outdoor recommendation (pair it with <strong>Counter-Shine</strong> for added gloss).</div>' };
    }
    const sqFt = num(State.answers.sqFt);
    if (sqFt <= 0) return null;
    const lines = [`Based on <strong>${sqFt} sq ft</strong>${loc ? `, <strong>${loc}</strong>` : ''}${finish ? `, <strong>${finish === 'satin' ? 'Satin' : finish === 'semigloss' ? 'Semi-Gloss' : 'High-Gloss'}</strong>` : ''}:`];
    if (loc === 'outdoor') {
      lines.push(`• For outdoor projects we recommend <strong>SiAcryl 14</strong>${finish === 'semigloss' ? ' paired with <strong>Counter-Shine</strong> for added gloss' : ''}.`);
    }
    const visible = productsForSection('sealers');
    visible.forEach(p => {
      const rec = recommendedQtyFor(p, 'sealers');
      if (rec != null && rec > 0) {
        const unitLabel = p.handle === 'ultra-z-poxy' ? 'kit' : (p.handle === 'z-aqua-poxy-2-qt-epoxy-sealer' ? 'kit' : 'unit');
        lines.push(`• <strong>${p.title}:</strong> ~${rec} ${unitLabel}${rec > 1 ? 's' : ''}`);
      }
    });
    // Sealer pairing rule + Counter-Shine warning
    lines.push('<div class="ctw-warn">⚠️ Choose <strong>just one sealer</strong> — the only exception is <strong>SiAcryl 14 + Counter-Shine</strong>, which can be paired (Counter-Shine layers on top to increase gloss). <em>Counter-Shine is a superficial layer that can only be used over SiAcryl 14 to increase the gloss.</em></div>');
    if (lines.length === 2) return null; // Only the heading + warning, no recommendations yet
    return { html: lines.join('<br>') };
  }

  function recommendTexturing() {
    const wantEdge = State.answers.textureEdge === 'yes';
    const wantTop = State.answers.textureTop === 'yes';
    if (!wantEdge && !wantTop) return null;
    const lines = [];
    const formType = detectFormTypeInCart();
    if (wantEdge) {
      if (!formType) lines.push(`• Select a Square Edge form in Section 1 — we'll then narrow the form-liner sizes for you.`);
      else if (formType === 'standard') lines.push(`• Form liners shown match your <strong>Standard (2-1/4")</strong> forms. Add <strong>Polyester Mounting Tape</strong> to adhere them to the form.`);
      else if (formType === 'euro') lines.push(`• Form liners shown match your <strong>EuroForm (1-1/4")</strong> forms. Add <strong>Polyester Mounting Tape</strong> to adhere them to the form.`);
      else if (formType === 'commercial') lines.push(`• Form liners shown match your <strong>Commercial (3-1/2")</strong> forms. Add <strong>Polyester Mounting Tape</strong> to adhere them to the form.`);
    }
    if (wantTop) {
      const liner = detectFormLinerInCart();
      if (liner === 'woodgrain') lines.push(`• Texture mats narrowed to <strong>Woodgrain</strong> only (to match your woodgrain form liner).`);
      else if (liner === 'other' || liner === 'both') lines.push(`• Woodgrain texture mat hidden (you've selected a stone-style form liner). Pick a stone-style mat to match.`);
      lines.push(`• Add <strong>Z Release</strong> — required as a release agent when pressing texture mats into the top.`);
    }
    if (!lines.length) return null;
    return { html: lines.join('<br>') };
  }

  function recommendSanding() {
    const wp = State.answers.wetPolish;
    if (wp === 'yes') {
      const polisher = prefersThreeHeadPolisher()
        ? `<strong>3 Head Polisher</strong> (faster and flatter on projects over ${THREE_HEAD_POLISHER_MIN_SQFT} sq ft)`
        : '<strong>Wet Polisher</strong>';
      const threeHeadPads = usesThreeHeadPolisher()
        ? ' With the 3 Head Polisher, get <strong>3 packs</strong> of Diamond Polishing Pads — one full 50–3000 grit set per head (its included polishing pads are 150 grit only).'
        : '';
      return { html: `Recommended for wet polishing: ${polisher} + <strong>Diamond Polishing Pads</strong>.${threeHeadPads} The Gem-Pad is always useful for easing edges.` };
    }
    if (wp === 'no') {
      return { html: 'For dry sanding choose <strong>either</strong> the Grit-Disc <em>or</em> the Diamond-Disc — not both. Use the <strong>Grit-Disc</strong> for light sanding and to remove dustiness, or the <strong>Diamond-Disc</strong> for more intense sanding and minor aggregate exposure. The Gem-Pad is always useful for easing edges.' };
    }
    return null;
  }

  function recommendTools() {
    const need = State.answers.needFinishingTools;
    const mixTypes = detectMixTypesInCart();
    const lines = [];
    if (need === 'yes') {
      if (mixTypes.white > 0) {
        lines.push('Recommended finishing tools for <strong>White Countertop Mix</strong>: Lexan Float, Chrome Finishing Trowel, Margin Trowel, and Trowel-Slick.');
      } else {
        // Liqui-Crete OR no mix in cart.
        lines.push('Recommended finishing tools for <strong>Z Liqui-Crete</strong>: Magnesium Float, Stainless Finishing Trowel, Margin Trowel, and Trowel-Slick.');
      }
    } else if (need === 'no') {
      lines.push('Skipping finishing tools. We still recommend a mixer, the Vibra-Blade, and Trowel-Slick regardless.');
    } else {
      return null;
    }
    return { html: lines.join('<br>') };
  }

  function recommendColoring() {
    const sqFt = num(State.answers.sqFt);
    if (sqFt <= 0) return null;
    const aquaBottles = Math.ceil(sqFt / COVERAGE.aquaSqftPerBottle);
    // Estimate mix bags for Terra-Tint sizing — re-use mix calc.
    const mixRec = recommendMix();
    const totalBags = mixRec ? Math.max(mixRec.whiteBags, mixRec.lcBags || 0) : 0;
    const terraBucketsLow = totalBags > 0 ? Math.ceil(totalBags / COVERAGE.terraTintBagsPerBucket) : 0;
    const terraBucketsHigh = totalBags > 0 ? Math.ceil((totalBags * 4) / 20) : 0; // up to 4 scoops/bag, 20 scoops/bucket
    return {
      html: `Based on <strong>${sqFt} sq ft</strong>:<br>` +
            `• <strong>Aqua-Stain UV / Aqua-Tint:</strong> ~${aquaBottles} bottle${aquaBottles > 1 ? 's' : ''} (typical coverage is 50–75 sq ft per bottle; coverage varies with concrete porosity and application method).<br>` +
            `• <strong>Terra-Tint (integral):</strong> ~${terraBucketsLow} bucket${terraBucketsLow > 1 ? 's' : ''} for standard color, up to ${terraBucketsHigh} bucket${terraBucketsHigh > 1 ? 's' : ''} for the darkest color. ~20 scoops per bucket; 1–4 scoops per bag of mix.<br>` +
            `• <strong>Color-Pack:</strong> 1 box per bag of mix. Do not exceed.`,
      aquaBottles
    };
  }

  // Per-product recommended quantity (returned as integer or null if not applicable)
  function recommendedQtyFor(product, section) {
    if (!product) return null;
    if (section === 'mix') {
      const r = recommendMix(); if (!r) return null;
      if (product.tags.indexOf('wizard:mix-white') !== -1) return r.whiteBags;
      if (product.tags.indexOf('wizard:mix-liquicrete-single') !== -1) {
        if (r.formType === 'euro') return 0;
        return r.lcBags % 6;
      }
      if (product.tags.indexOf('wizard:mix-liquicrete-6pack') !== -1) {
        if (r.formType === 'euro') return 0;
        return Math.floor(r.lcBags / 6);
      }
    }
    if (section === 'accessories') {
      // Edge Expert Set: recommended whenever a non-square-edge form profile is
      // in the cart (independent of the qty inputs recommendAccessories uses).
      if (product.handle === EDGE_EXPERT_HANDLE) {
        return hasNonSquareEdgeFormInCart() ? 1 : null;
      }
      const r = recommendAccessories(); if (!r) return null;
      if (product.tags.indexOf('wizard:reinforcement') !== -1) return r.meshRolls;
      if (product.tags.indexOf('wizard:clip') !== -1) return r.clipBoxes;
      if (product.tags.indexOf('wizard:sinkform') !== -1) return State.answers.sink === 'yes' ? 1 : 0;
      if (product.tags.indexOf('wizard:faucet-knockout-standard') !== -1 || product.tags.indexOf('wizard:faucet-knockout-euro') !== -1) {
        return State.answers.sink === 'yes' ? num(State.answers.faucetHoles) : 0;
      }
      if (product.tags.indexOf('wizard:screws') !== -1) return r.screwBoxes;
    }
    if (section === 'sealers') {
      const sqFt = num(State.answers.sqFt);
      if (sqFt <= 0) return null; // no numeric quantity without a sq ft value
      // Outdoor: which products are recommended is driven by location + finish
      // (see isRecommendedSealer). Quantity is still coverage-based here.
      if (State.answers.useLocation === 'outdoor') {
        if (!isRecommendedSealer(product)) return null;
        const coverage = SEALER_COVERAGE[product.handle];
        return coverage ? Math.ceil(sqFt / coverage) : null;
      }
      // Indoor / no location chosen — unchanged coverage-based logic.
      // Counter-Shine: only recommended when the customer picked Semi-Gloss.
      if (product.handle === 'z-counter-shine-polish') {
        if (State.answers.sealerFinish !== 'semigloss') return null;
        const coverage = SEALER_COVERAGE['z-counter-shine-polish'];
        return Math.ceil(sqFt / coverage);
      }
      if (product.handle === 'ultra-z-poxy') {
        // variant-based coverage
        return null;
      }
      const coverage = SEALER_COVERAGE[product.handle];
      if (!coverage) return null;
      return Math.ceil(sqFt / coverage);
    }
    if (section === 'texturing') {
      if (product.tags.indexOf('wizard:mounting-tape') !== -1) return 1;
      if (product.tags.indexOf('wizard:release') !== -1) return 1;
      // Form liners: qty = ceil(profileFt / piece length)
      if (product.tags.indexOf('wizard:formliner') !== -1) {
        const profile = num(State.answers.profileFt);
        const len = FORM_LINER_LENGTH_FT[product.handle];
        if (profile > 0 && len) return Math.ceil(profile / len);
        return null;
      }
      return null;
    }
    if (section === 'sanding') {
      const tags = product.tags || [];
      const wp = State.answers.wetPolish;
      // Texture mat in cart: the Grit-Disc is the ONLY recommended sanding
      // product, regardless of the wet-polish answer. The wet-polish tools may
      // still be shown (if wetPolish=yes) but are never badged here.
      if (hasTextureMatInCart()) {
        return tags.indexOf('wizard:sand-gritdisc') !== -1 ? 1 : null;
      }
      // Gem-Pad: always shown, never auto-recommended
      if (tags.indexOf('wizard:sand-gempad') !== -1) return null;
      if (wp === 'yes') {
        // Exactly one polisher is badged: the 3 Head Polisher on projects over
        // 50 sq ft, otherwise (incl. no sq ft entered) the Wet Polisher.
        const threeHead = prefersThreeHeadPolisher();
        if (tags.indexOf('wizard:sand-wetpolisher') !== -1) return threeHead ? null : 1;
        if (tags.indexOf('wizard:sand-3headpolisher') !== -1) return threeHead ? 1 : null;
        if (tags.indexOf('wizard:sand-polishpads') !== -1) return usesThreeHeadPolisher() ? 3 : 1;
      }
      if (wp === 'no') {
        // For the multi-variant abrasives we return a positive value so the
        // "Recommended" badge appears, but the text is overridden via
        // RECOMMENDED_LABEL_OVERRIDES (no numeric qty applied to the input).
        if (tags.indexOf('wizard:sand-gritdisc') !== -1) return 1;
        if (tags.indexOf('wizard:sand-diamonddisc') !== -1) return 1;
      }
      return null;
    }
    if (section === 'brackets' || section === 'grills') {
      return null; // never auto-recommend any specific bracket / grill
    }
    if (section === 'tools') {
      const tags = product.tags || [];
      // Trowel-Slick: always recommended
      if (tags.indexOf('wizard:tool-trowelslick') !== -1) return 1;
      // Everything else only recommended when customer says they need finishing tools
      if (State.answers.needFinishingTools !== 'yes') return null;
      // Margin Trowel: recommended for BOTH mix sets.
      if (tags.indexOf('wizard:tool-margin') !== -1) return 1;
      // White Mix → White set; Liqui-Crete OR no mix → Liqui-Crete set.
      const types = detectMixTypesInCart();
      const isWhite = types.white > 0;
      if (isWhite) {
        if (tags.indexOf('wizard:tool-lexanfloat') !== -1) return 1;
        if (tags.indexOf('wizard:tool-chrome') !== -1) return 1;
      } else {
        if (tags.indexOf('wizard:tool-magfloat') !== -1) return 1;
        // Liqui-Crete pairing uses ONLY the 16"x5" Stainless Trowel (not the 12"x5").
        if (tags.indexOf('wizard:tool-stainless') !== -1) {
          return product.handle === 'stainless-finishing-trowel-16-x-5-gold-series' ? 1 : null;
        }
      }
      // Screeds, Vibra-Blade, Mixers: shown, never auto-recommended.
      return null;
    }
    if (section === 'coloring') {
      const r = recommendColoring();
      if (product.tags.indexOf('wizard:color-aquastain') !== -1) return r ? r.aquaBottles : null;
      if (product.tags.indexOf('wizard:color-aquatint') !== -1) return r ? r.aquaBottles : null;
      if (product.tags.indexOf('wizard:color-terratint') !== -1) {
        // Recommended only when customer is NOT using White Countertop Mix
        // (Color-Pack is the dedicated integral for White Mix).
        const types = detectMixTypesInCart();
        if (types.white > 0) return null;
        const bags = types.liquicrete > 0
          ? types.liquicrete
          : (recommendMix() ? Math.max(recommendMix().whiteBags, recommendMix().lcBags || 0) : 0);
        if (bags <= 0) return null;
        return Math.ceil(bags / COVERAGE.terraTintBagsPerBucket);
      }
      if (product.tags.indexOf('wizard:color-colorpack') !== -1) {
        // Recommended only when customer IS using White Countertop Mix.
        const types = detectMixTypesInCart();
        if (types.white <= 0) return null;
        return types.white;
      }
      return null;
    }
    return null;
  }

  // Which OUTDOOR sealers carry the Recommended badge, by location + finish.
  // (Indoor uses the coverage-based logic in recommendedQtyFor.) This is
  // independent of sq ft, so the badge shows as soon as the customer picks a
  // finish — the quantity fills in once they enter sq ft.
  //   outdoor + (no finish) → SiAcryl 14 + Counter-Shine
  //   outdoor + satin       → SiAcryl 14
  //   outdoor + semi-gloss  → SiAcryl 14 + Counter-Shine
  //   outdoor + high-gloss  → none (the section shows nothing)
  function isRecommendedSealer(product) {
    if (State.answers.useLocation !== 'outdoor') return false;
    const isSiacryl = product.handle === 'z-siacryl-14-sealer';
    const isCounterShine = product.handle === 'z-counter-shine-polish';
    const finish = State.answers.sealerFinish;
    if (finish === 'satin') return isSiacryl;
    if (finish === 'semigloss') return isSiacryl || isCounterShine;
    if (!finish) return isSiacryl || isCounterShine;
    return false; // highgloss → nothing recommended
  }

  // Unified "should this card show the Recommended badge / sort first" test.
  // Sealers add the sq-ft-independent outdoor rule on top of the quantity rule;
  // every other section is purely quantity-driven (unchanged behavior).
  function isRecommended(product, section) {
    if (section === 'sealers' && isRecommendedSealer(product)) return true;
    const q = recommendedQtyFor(product, section);
    return q != null && q > 0;
  }

  // For products with a variant axis (e.g., screws Euro vs Standard, faucet KO), pre-select the right variant.
  function defaultVariantFor(product) {
    if (!product.variants || !product.variants.length) return null;
    const cartFormType = detectFormTypeInCart();
    // Forms: pre-select Full Package if profile or backwall > 30 ft, else Half Package
    if (product.tags.indexOf('wizard:forms') !== -1 && product.variants.length > 1) {
      const needed = Math.max(num(State.answers.profileFt), num(State.answers.backwallFt));
      const preferFull = needed > 30;
      const fullV = product.variants.find(v => /full/i.test(v.title));
      const halfV = product.variants.find(v => /half/i.test(v.title));
      if (preferFull && fullV) return fullV;
      if (!preferFull && halfV) return halfV;
    }
    if (product.tags.indexOf('wizard:screws') !== -1) {
      if (cartFormType === 'euro') {
        const v = product.variants.find(v => /3\/8|euro/i.test(v.title) || /3\/8|euro/i.test(v.sku || ''));
        if (v) return v;
      } else {
        const v = product.variants.find(v => /5\/8|standard/i.test(v.title) || /5\/8|standard/i.test(v.sku || ''));
        if (v) return v;
      }
    }
    // Form liners: pick the variant matching the customer's form type
    if (product.tags.indexOf('wizard:formliner') !== -1 && product.variants.length > 1) {
      const pattern = formLinerVariantPattern();
      if (pattern) {
        const v = product.variants.find(v => pattern.test(v.title || ''));
        if (v) return v;
      }
    }
    // Z Clips: pick EuroForm variant (EZC-001) if Euroforms are in cart, else Standard (ZC-002)
    if (product.tags.indexOf('wizard:clip') !== -1) {
      if (cartFormType === 'euro') {
        const v = product.variants.find(v => /euro/i.test(v.title) || /^EZC/i.test(v.sku || ''));
        if (v) return v;
      } else {
        const v = product.variants.find(v => /standard|all other/i.test(v.title) || /^ZC-/i.test(v.sku || ''));
        if (v) return v;
      }
    }
    // Default: first available variant
    return product.variants.find(v => v.available) || product.variants[0];
  }

  // -------------------- Rendering: product cards --------------------
  // Track previous recommended qty so we can preserve user-customized qty across re-renders.
  const prevRecMap = new Map();
  // Persist user-selected variant choice across re-renders (productId -> variantId).
  const userVariantMap = new Map();
  // Persist multi-variant selections across re-renders (productId -> Set of variantId).
  const multiVariantMap = new Map();

  // Compute the default multi-variant selection for a product (by matching
  // variant titles against the configured defaults), used on first render.
  function defaultMultiSelection(product) {
    const patterns = MULTI_VARIANT_DEFAULTS[product.handle];
    const set = new Set();
    if (!patterns) return set;
    product.variants.forEach(v => {
      if (patterns.indexOf(v.title) !== -1) set.add(v.id);
    });
    return set;
  }
  function getMultiSelection(product) {
    if (multiVariantMap.has(product.id)) return multiVariantMap.get(product.id);
    const def = defaultMultiSelection(product);
    multiVariantMap.set(product.id, def);
    return def;
  }

  function renderProductCard(product, section) {
    if (!product) return '';
    const recQty = recommendedQtyFor(product, section);
    // Badge / sort-first status. Usually this is just "has a recommended qty",
    // but sealers can be recommended (by location + finish) before a sq ft
    // value makes a quantity available.
    const recommended = isRecommended(product, section);
    const defaultVariant = defaultVariantFor(product);
    const hasVariants = !product.hasOnlyDefaultVariant && product.variants.length > 1;
    const isMultiVariant = hasVariants && (product.tags || []).indexOf('wizard:multi-variant') !== -1;
    const isBracket = section === 'brackets' && hasVariants && product.optionsWithValues && product.optionsWithValues.length > 1;
    const isColoring = section === 'coloring' && hasVariants && !isMultiVariant && !isBracket;

    // Decide which variant is selected: user choice if they made one, else default
    let selectedVariant = defaultVariant;
    const userChoiceId = userVariantMap.get(product.id);
    if (userChoiceId) {
      const found = product.variants.find(v => v.id === userChoiceId);
      if (found) selectedVariant = found;
    }
    // Variants the customer may actually pick (e.g. Color-Pack hides Black
    // outdoors). If the current selection was filtered out, fall back to the
    // first visible variant so the card never shows/adds a hidden color.
    const visibleVariants = visibleVariantsFor(product);
    if (visibleVariants.length && selectedVariant && !visibleVariants.some(v => v.id === selectedVariant.id)) {
      selectedVariant = visibleVariants[0];
    }

    // Forms auto-fill the qty box when a single package size is recommended
    // (see formRecQty); other products keep their normal recommended qty.
    const formAutoQty = formRecQty(product, section);
    const effectiveRec = formAutoQty != null ? formAutoQty : recQty;

    // Preserve user-customized qty across re-renders.
    // Only treat the existing qty as "user-customized" if the previous render
    // had a meaningful (> 0) recommendation. Otherwise the existing qty is just
    // the fallback default of 1 and we should let the new recommendation win.
    const existingCard = root.querySelector(`.ctw-product[data-product-id="${product.id}"]`);
    const existingQty = existingCard ? num(existingCard.querySelector('.ctw-product__qty')?.value) : 0;
    const prevRec = prevRecMap.get(product.id);
    let initialQty;
    if (existingQty > 0 && prevRec != null && prevRec > 0 && existingQty !== prevRec) {
      // User customized — keep their value
      initialQty = existingQty;
    } else {
      initialQty = (effectiveRec != null && effectiveRec > 0) ? effectiveRec : 1;
    }
    prevRecMap.set(product.id, effectiveRec);

    // Single-select cards (dropdown / swatches / single-variant) gray out Add
    // when the SELECTED variant is sold out — the customer must pick an in-stock
    // option. (Multi-variant disables sold-out checkboxes instead; brackets
    // manage their own Add state once an option combo is matched.)
    const selectedOOS = !isMultiVariant && !isBracket && !!selectedVariant && !selectedVariant.available;
    const outOfStock = !product.available || selectedOOS;

    // ---------- Variant UI ----------
    let variantHtml = '';
    if (isMultiVariant) {
      const selectedIds = getMultiSelection(product);
      const items = product.variants.map(v => {
        const isSel = selectedIds.has(v.id);
        const oos = !v.available;
        return `<label class="ctw-multi-variant${oos ? ' is-sold-out' : ''}">
          <input type="checkbox" class="ctw-multi-variant__cb" data-variant-id="${v.id}" data-product-id="${product.id}"${isSel ? ' checked' : ''}${oos ? ' disabled' : ''}>
          <span class="ctw-multi-variant__title">${escapeHtml(v.title)}${oos ? ' (Sold out)' : ''}</span>
          <span class="ctw-multi-variant__price">${escapeHtml(v.priceFormatted)}</span>
        </label>`;
      }).join('');
      variantHtml = `<div class="ctw-product__variant ctw-product__variant--multi">
        <div class="ctw-multi-variant__heading">Select grits</div>
        <div class="ctw-multi-variants">${items}</div>
      </div>`;
    } else if (hasVariants) {
      const opts = visibleVariants.map(v => {
        const oosLabel = !v.available ? ' (Sold out)' : '';
        const isSel = selectedVariant && v.id === selectedVariant.id ? ' selected' : '';
        return `<option value="${v.id}"${isSel}>${escapeHtml(v.title)} — ${escapeHtml(v.priceFormatted)}${oosLabel}</option>`;
      }).join('');

      if (isBracket) {
        // Per-option dropdowns: Thickness, Length, (Height)
        const sortedOpts = product.optionsWithValues.slice().sort((a, b) => a.position - b.position);
        const optHtml = sortedOpts.map(opt => {
          const items = opt.values.map(val =>
            `<option value="${escapeHtml(val)}">${escapeHtml(val)}</option>`
          ).join('');
          return `<div class="ctw-bracket-opt">
            <label>${escapeHtml(opt.name)}</label>
            <select class="ctw-bracket-select" data-option-position="${opt.position}" data-product-id="${product.id}">
              <option value="">— Select —</option>
              ${items}
            </select>
          </div>`;
        }).join('');
        variantHtml = `<div class="ctw-product__variant ctw-product__variant--bracket">
          ${optHtml}
          <div class="ctw-bracket-status" data-bracket-status="${product.id}"></div>
          <select class="ctw-product__variant-select" data-product-id="${product.id}" hidden aria-hidden="true">${opts}</select>
        </div>`;
      } else if (isColoring) {
        // Color swatches: small clickable thumbnails that drive a hidden select
        const swatches = visibleVariants.map(v => {
          const isSel = selectedVariant && v.id === selectedVariant.id;
          const oos = !v.available;
          const img = v.image ? `<img src="${escapeHtml(v.image)}" alt="${escapeHtml(v.title)}">` : '';
          return `<button type="button" class="ctw-swatch${isSel ? ' is-selected' : ''}${oos ? ' is-sold-out' : ''}" data-variant-id="${v.id}" title="${escapeHtml(v.title)}${oos ? ' (Sold out)' : ''}" aria-label="${escapeHtml(v.title)}">${img}</button>`;
        }).join('');
        variantHtml = `<div class="ctw-product__variant ctw-product__variant--swatches">
          <div class="ctw-swatches">${swatches}</div>
          <div class="ctw-swatch__selected-name">Selected: <strong>${escapeHtml(selectedVariant.title)}</strong></div>
          <select class="ctw-product__variant-select" data-product-id="${product.id}" hidden aria-hidden="true">${opts}</select>
        </div>`;
      } else {
        // Standard dropdown
        variantHtml = `<div class="ctw-product__variant">
          <label for="ctw-variant-${product.id}">Variant</label>
          <select id="ctw-variant-${product.id}" class="ctw-product__variant-select" data-product-id="${product.id}">${opts}</select>
        </div>`;
      }
    }

    const recLabel = RECOMMENDED_LABEL_OVERRIDES[product.handle];
    // Show "Recommended: N" when we know the quantity, else a plain
    // "Recommended" line for items recommended before sq ft is entered.
    const recBadge = recommended
      ? `<div class="ctw-product__rec">${recLabel ? escapeHtml(recLabel) : (recQty != null && recQty > 0 ? 'Recommended: ' + recQty : 'Recommended')}</div>` : '';

    // One-click "add both packages" block, only on a form card whose
    // recommendation is a Full + Half combination. The breakdown text comes from
    // the SAME packPartsText source as the badge, so they can't drift. The manual
    // dropdown + qty stay below for a custom single add.
    const combo = section === 'forms' ? formComboPlan(product) : null;
    const comboHtml = combo
      ? `<div class="ctw-product__combo">
          <div class="ctw-product__combo-note">This recommendation needs two package sizes:</div>
          <button type="button" class="ctw-product__add-combo" data-add-combo="${product.id}">Add recommended package: ${escapeHtml(combo.text)}</button>
          <div class="ctw-product__combo-or">or choose a size below</div>
        </div>`
      : '';
    // Commercial Bar Form covers the front edge only — flag that it has no backwall form.
    const formWarning = product.handle === COMMERCIAL_FORM_HANDLE
      ? '<div class="ctw-warn ctw-product__warn">⚠️ This form package does not contain backwall form.</div>'
      : '';
    // 3 Head Polisher: spell out what's in the box so customers can decide on
    // extra pads (the included pads are 150 grit only).
    const productNote = (product.tags || []).indexOf('wizard:sand-3headpolisher') !== -1
      ? '<div class="ctw-product__note"><strong>In the box:</strong> water/dust shroud with adapters, 3 × 4" grinding cup wheels, 3 × 4" foam backer pads, 3 × 150-grit 4" diamond polishing pads, 6 sandpaper discs, 2 replacement motor brushes, and a hex key &amp; shaft wrench. To polish through the full 50–3000 grit range, add <strong>3 packs of Diamond Polishing Pads</strong> (one set per head).</div>'
      : '';

    const maxQty = (selectedVariant && selectedVariant.inventoryManagement === 'shopify' && selectedVariant.inventoryPolicy !== 'continue')
      ? Math.max(1, selectedVariant.inventoryQuantity) : '';

    const imageUrl = (selectedVariant && selectedVariant.image) || product.image || '';
    const imageHoverUrl = product.image2 || '';
    // Color products: don't show secondary-image-on-hover (variant swatch IS the primary image)
    const imageHtml = imageUrl
      ? `<img class="ctw-product__img ctw-product__img--primary" src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.title)}" loading="lazy">` +
        (!isColoring && imageHoverUrl ? `<img class="ctw-product__img ctw-product__img--hover" src="${escapeHtml(imageHoverUrl)}" alt="" loading="lazy" aria-hidden="true">` : '')
      : '';

    return `
      <article class="ctw-product${recommended ? ' is-recommended' : ''}${combo ? ' is-combo' : ''}" data-product-id="${product.id}">
        ${recommended ? '<span class="ctw-product__badge">Recommended</span>' : ''}
        <div class="ctw-product__image-wrap">
          <a href="${escapeHtml(product.url)}" data-ctw-quickview-open="${escapeHtml(product.handle)}" aria-label="Quick view: ${escapeHtml(product.title)}">${imageHtml}</a>
        </div>
        <h3 class="ctw-product__title"><a href="${escapeHtml(product.url)}" data-ctw-quickview-open="${escapeHtml(product.handle)}">${escapeHtml(product.title)}</a></h3>
        <div class="ctw-product__price">${escapeHtml((selectedVariant && selectedVariant.priceFormatted) || product.priceFormatted || money(product.price))}</div>
        ${formWarning}
        ${productNote}
        ${recBadge}
        ${comboHtml}
        ${variantHtml}
        <div class="ctw-product__qty-row">
          <label for="ctw-qty-${product.id}">Qty</label>
          <input type="number" id="ctw-qty-${product.id}" class="ctw-product__qty" value="${initialQty}" min="1"${maxQty ? ` max="${maxQty}"` : ''}>
        </div>
        ${outOfStock ? '<div class="ctw-product__stockmsg is-error">Out of stock</div>' : ''}
        <button type="button" class="ctw-product__add" data-add-product="${product.id}"${isMultiVariant ? ' data-multi-variant="1"' : ''}${(outOfStock || isBracket) ? ' disabled' : ''}>
          ${outOfStock ? 'Out of stock' : (isBracket ? 'Select options first' : (isMultiVariant ? 'Add Selected to Cart' : 'Add to Cart'))}
        </button>
      </article>`;
  }

  function renderSection(section) {
    const container = root.querySelector(`[data-products="${section}"]`);
    if (!container) return;
    const list = productsForSection(section);
    // Sort: products with a positive recommended qty come first; otherwise
    // preserve the original (collection / popularity) order. Stable in modern browsers.
    const sorted = list.slice().sort((a, b) => {
      const aHas = isRecommended(a, section) ? 1 : 0;
      const bHas = isRecommended(b, section) ? 1 : 0;
      return bHas - aHas;
    });
    if (!sorted.length) {
      container.innerHTML = '<p style="opacity:0.6;font-size:14px">No products available for your current selections.</p>';
    } else {
      container.innerHTML = sorted.map(p => renderProductCard(p, section)).join('');
    }
    // Recommendation banner
    const recEl = root.querySelector(`[data-recommendation="${section}"]`);
    if (recEl) {
      const rec = section === 'forms' ? recommendForms()
                : section === 'mix' ? recommendMix()
                : section === 'accessories' ? recommendAccessories()
                : section === 'coloring' ? recommendColoring()
                : section === 'sealers' ? recommendSealers()
                : section === 'texturing' ? recommendTexturing()
                : section === 'tools' ? recommendTools()
                : section === 'sanding' ? recommendSanding()
                : null;
      if (rec && rec.html) {
        const subtitle = section === 'mix'
          ? ' <span class="ctw-recommendation__subtitle">— Select <em>either</em> White Countertop Mix or Liqui-Crete (not both)</span>'
          : '';
        recEl.innerHTML = '<div class="ctw-recommendation__title">Recommended based on provided details' + subtitle + '</div>' + rec.html;
        recEl.hidden = false;
      } else {
        recEl.hidden = true;
        recEl.innerHTML = '';
      }
    }
  }

  function renderAllSections() { SECTIONS.forEach(renderSection); }

  // -------------------- Cart panel rendering --------------------
  function renderCartPanel() {
    const items = State.cart.items || [];
    const countEl = $('#ctw-cart-count');
    const subEl = $('#ctw-cart-subtotal');
    const sub2El = $('#ctw-cart-subtotal-2');
    const itemsEl = $('#ctw-cart-items');
    const emptyEl = $('#ctw-cart-empty');

    const count = items.reduce((acc, i) => acc + i.quantity, 0);
    countEl.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
    subEl.textContent = money(State.cart.total_price || 0);
    if (sub2El) sub2El.textContent = money(State.cart.total_price || 0);

    if (!items.length) {
      emptyEl.classList.remove('is-hidden');
      itemsEl.innerHTML = '';
      return;
    }
    emptyEl.classList.add('is-hidden');
    itemsEl.innerHTML = items.map((item, idx) => `
      <li class="ctw__cart-item">
        ${item.image ? `<img class="ctw__cart-item-img" src="${escapeHtml(item.image)}" alt="">` : '<div class="ctw__cart-item-img"></div>'}
        <div class="ctw__cart-item-info">
          <div class="ctw__cart-item-title">${escapeHtml(item.product_title)}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="ctw__cart-item-variant">${escapeHtml(item.variant_title)}</div>` : ''}
          <div class="ctw__cart-item-meta">
            <span>Qty ${item.quantity} · ${money(item.final_line_price)}</span>
            <button type="button" class="ctw__cart-item-remove" data-remove-variant="${item.id}">Remove</button>
          </div>
        </div>
      </li>`).join('');
  }

  // -------------------- Event handlers --------------------
  function bindQuestionInputs() {
    // sqFt syncs across all sections that ask for it. Same for any other shared keys.
    $$('input[data-answer], select[data-answer]').forEach(el => {
      const key = el.dataset.answer;
      // Set initial value from state
      if (State.answers[key] !== undefined && State.answers[key] !== '' && State.answers[key] !== 0) {
        el.value = State.answers[key];
      }
      el.addEventListener('input', debounce(() => {
        const val = el.type === 'number' ? num(el.value) : el.value;
        State.answers[key] = val;
        // Sync all inputs with the same data-answer key
        $$(`[data-answer="${key}"]`).forEach(other => {
          if (other !== el) other.value = el.value;
        });
        saveState();
        applyConditionalFields();
        renderAllSections();
      }, 200));
    });
    applyConditionalFields();
  }

  function applyConditionalFields() {
    $$('[data-show-if]').forEach(el => {
      const cond = el.dataset.showIf; // e.g., "sink=yes"
      const [key, val] = cond.split('=');
      el.classList.toggle('is-visible', String(State.answers[key]) === val);
    });
  }

  // Cross-product compatibility warnings, shared by the section cards and the
  // quick-view add bar. Returns false if the customer cancels any warning.
  function runAddCompatibilityChecks(product) {
    const pTags = product.tags || [];
    const isWhiteMix     = pTags.indexOf('wizard:mix-white') !== -1;
    const isLiquiCrete   = pTags.indexOf('wizard:mix-liquicrete') !== -1;
    const isCounterShine = pTags.indexOf('wizard:sealer-countershine') !== -1;
    const isSiAcryl      = product.handle === 'z-siacryl-14-sealer';
    const isOtherSealer  = pTags.indexOf('wizard:sealers') !== -1 && !isCounterShine && !isSiAcryl;
    const isFormLiner    = pTags.indexOf('wizard:formliner') !== -1;
    const isWetPolishProduct = pTags.indexOf('wizard:sand-wetpolisher') !== -1 || pTags.indexOf('wizard:sand-3headpolisher') !== -1 || pTags.indexOf('wizard:sand-polishpads') !== -1;
    const isTextureMat   = pTags.indexOf('wizard:texturemat') !== -1;
    const isEdgeIncompatibleForm = pTags.indexOf('wizard:form-euro') !== -1 || pTags.indexOf('wizard:form-commercial') !== -1;

    // Edge Expert Set works only with Standard Height Edge Forms.
    if (isEdgeIncompatibleForm && hasEdgeExpertInCart()) {
      if (!confirm(
        'Heads up — EuroForms and Commercial forms are not compatible with ' +
        'the Z Edge Expert Set (it only works with Standard Height Edge ' +
        'Forms). Do you want to add this form anyway?'
      )) return false;
    }

    // Texture mat ↔ wet polishing are mutually exclusive.
    if ((isWetPolishProduct && hasTextureMatInCart()) || (isTextureMat && hasWetPolishProductInCart())) {
      if (!confirm(
        'Since you are stamping the top, you will not be able to wet polish ' +
        'without removing the texture. Do you still want to add products to ' +
        'your cart?'
      )) return false;
    }

    // Mix exclusivity — White Mix vs Liqui-Crete
    const mixes = getMixesInCart();
    if (isWhiteMix && mixes.hasLC) {
      if (!confirm(
        'You are attempting to add White Countertop Mix to your cart, but ' +
        'you already have Z Liqui-Crete. These products are not used ' +
        'together — you should pick one or the other.\n\n' +
        'Do you want to proceed with adding White Countertop Mix?'
      )) return false;
    }
    if (isLiquiCrete && mixes.hasWhite) {
      if (!confirm(
        'You are attempting to add Z Liqui-Crete to your cart, but you ' +
        'already have White Countertop Mix. These products are not used ' +
        'together — you should pick one or the other.\n\n' +
        'Do you want to proceed with adding Z Liqui-Crete?'
      )) return false;
    }

    // Counter-Shine compatibility — only pairs with SiAcryl 14
    const sealers = getSealersInCart();
    if (isCounterShine && sealers.other) {
      if (!confirm(
        'You are attempting to add Counter-Shine, but the sealer already ' +
        'in your cart is not compatible. Counter-Shine can only be used ' +
        'as a top layer over SiAcryl 14 to increase gloss.\n\n' +
        'Do you want to proceed with adding Counter-Shine anyway?'
      )) return false;
    }
    if (isOtherSealer && sealers.countershine) {
      if (!confirm(
        'You are attempting to add a sealer to your cart, but you already ' +
        'have Counter-Shine. Counter-Shine is only compatible with ' +
        'SiAcryl 14 — adding a different sealer means the Counter-Shine ' +
        'will not work as intended.\n\n' +
        'Do you want to proceed anyway?'
      )) return false;
    }

    // Form-liner compatibility — only fits Square Edge form profiles
    if (isFormLiner && hasIncompatibleFormInCart()) {
      if (!confirm(
        'Heads up — the edge profile in your cart is not a Square Edge ' +
        'form, so this form liner will not be compatible with it. Form ' +
        'liners only fit Square Edge, EuroForm Square Edge, or Commercial ' +
        'Bar forms.\n\n' +
        'Add the form liner anyway?'
      )) return false;
    }
    return true;
  }

  function bindProductActions() {
    root.addEventListener('click', async (e) => {
      // One-click "add recommended package" (Full + Half of one form, in a
      // single deferred add). Recompute the combo fresh — never trust stale DOM.
      const comboBtn = e.target.closest('[data-add-combo]');
      if (comboBtn) {
        const product = PRODUCTS.find(p => p.id === parseInt(comboBtn.dataset.addCombo, 10));
        if (!product) return;
        const combo = formComboPlan(product);
        if (!combo) return;
        if (!runAddCompatibilityChecks(product)) return;
        // Best-effort flash (the section re-render in enqueueAdd replaces this
        // card; the durable confirmation is both lines appearing in the panel).
        comboBtn.classList.add('is-added');
        comboBtn.textContent = '✓ Added';
        enqueueAdd([
          { id: combo.fullVariant.id, quantity: combo.fullCount },
          { id: combo.halfVariant.id, quantity: combo.halfCount },
        ]);
        return;
      }

      // Variant change updates the qty max and price display
      // Add to cart
      const addBtn = e.target.closest('[data-add-product]');
      if (addBtn) {
        const productId = parseInt(addBtn.dataset.addProduct, 10);
        const product = PRODUCTS.find(p => p.id === productId);
        if (!product) return;
        const card = addBtn.closest('.ctw-product');
        const qtyInput = card.querySelector('.ctw-product__qty');
        const qty = Math.max(1, num(qtyInput.value));
        const isMulti = addBtn.dataset.multiVariant === '1';

        let variantIdsToAdd = [];
        if (isMulti) {
          variantIdsToAdd = Array.from(card.querySelectorAll('.ctw-multi-variant__cb:checked'))
            .map(cb => parseInt(cb.dataset.variantId, 10));
          if (!variantIdsToAdd.length) {
            alert('Please check at least one option to add.');
            return;
          }
        } else {
          const variantSel = card.querySelector('.ctw-product__variant-select');
          const vid = variantSel ? parseInt(variantSel.value, 10) : (product.variants[0] && product.variants[0].id);
          variantIdsToAdd = [vid];
        }

        // Safety net: never enqueue a sold-out variant, even if the button state
        // is somehow stale. The selected/checked variants are all in stock here.
        if (variantIdsToAdd.some(id => {
          const v = product.variants.find(vv => vv.id === id);
          return !v || !v.available;
        })) {
          return;
        }

        // Cross-product compatibility warnings (shared with the quick-view add).
        if (!runAddCompatibilityChecks(product)) return;

        addBtn.disabled = true;
        const originalLabel = addBtn.textContent;
        // Instant, purely local add — the real /cart/add.js write happens once,
        // at a sync point (cart/checkout click, drawer open, or page leave).
        // See the deferred add queue. Zero cart-API traffic while picking
        // products = the Cloudflare rate-limit lockout can't trigger here.
        const items = variantIdsToAdd.map(vid => ({ id: vid, quantity: qty }));
        enqueueAdd(items);
        addBtn.classList.add('is-added');
        addBtn.textContent = variantIdsToAdd.length > 1 ? `✓ Added ${variantIdsToAdd.length}` : '✓ Added';
        setTimeout(() => {
          addBtn.classList.remove('is-added');
          addBtn.disabled = false;
          addBtn.textContent = originalLabel;
        }, 1500);
        return;
      }

      // Remove an item (by variant). The local pending portion is dropped with
      // zero network; only a portion that already lives in the SERVER cart
      // needs a single /cart/change.js call.
      const rmBtn = e.target.closest('[data-remove-variant]');
      if (rmBtn) {
        const vid = parseInt(rmBtn.dataset.removeVariant, 10);
        rmBtn.disabled = true;
        try {
          const qIdx = _addQueue.findIndex(q => q.id === vid);
          if (qIdx !== -1) {
            _addQueue.splice(qIdx, 1);
            persistPendingAdds();
          }
          const serverItems = (State.serverCart && State.serverCart.items) || [];
          const lineIdx = serverItems.findIndex(i => i.id === vid);
          if (lineIdx !== -1) {
            await changeLine(lineIdx + 1, 0); // refetches + recomputes merged view
            refreshThemeCartNow(); // keep the site cart drawer in sync
          } else {
            recomputeMergedCart();
          }
          renderCartPanel();
          renderAllSections();
        } catch (err) {
          console.error('[CTW] remove error', err);
          rmBtn.disabled = false; // let the customer retry (or see the banner)
          handleCartError(err);
        }
        return;
      }
    });

    // Variant change: update product image / price within the card
    root.addEventListener('change', (e) => {
      const sel = e.target.closest('.ctw-product__variant-select');
      if (!sel) return;
      const card = sel.closest('.ctw-product');
      const productId = parseInt(sel.dataset.productId, 10);
      const product = PRODUCTS.find(p => p.id === productId);
      const variant = product && product.variants.find(v => v.id === parseInt(sel.value, 10));
      if (!variant) return;
      // Remember the user's choice so re-renders preserve it
      userVariantMap.set(productId, variant.id);
      const priceEl = card.querySelector('.ctw-product__price');
      if (priceEl) priceEl.textContent = variant.priceFormatted;
      const imgEl = card.querySelector('.ctw-product__img--primary');
      if (imgEl && variant.image) imgEl.src = variant.image;
      const qtyEl = card.querySelector('.ctw-product__qty');
      if (qtyEl && variant.inventoryManagement === 'shopify' && variant.inventoryPolicy !== 'continue') {
        qtyEl.max = Math.max(1, variant.inventoryQuantity);
      } else if (qtyEl) {
        qtyEl.removeAttribute('max');
      }
      // Update "Selected: …" label for swatch variants
      const nameEl = card.querySelector('.ctw-swatch__selected-name strong');
      if (nameEl) nameEl.textContent = variant.title;
      // Gray out Add when the newly-selected variant is sold out (single-select
      // cards only — brackets/multi-variant manage their own Add state).
      const addBtn = card.querySelector('.ctw-product__add');
      if (addBtn && addBtn.dataset.multiVariant !== '1' && !card.querySelector('.ctw-bracket-select')) {
        const oos = !variant.available;
        addBtn.disabled = oos;
        addBtn.classList.remove('is-added');
        addBtn.textContent = oos ? 'Out of stock' : 'Add to Cart';
        let stockMsg = card.querySelector('.ctw-product__stockmsg');
        if (oos && !stockMsg) {
          stockMsg = document.createElement('div');
          stockMsg.className = 'ctw-product__stockmsg is-error';
          stockMsg.textContent = 'Out of stock';
          addBtn.parentNode.insertBefore(stockMsg, addBtn);
        } else if (!oos && stockMsg) {
          stockMsg.remove();
        }
      }
    });

    // Bracket per-option dropdown changes → find matching variant
    root.addEventListener('change', (e) => {
      const sel = e.target.closest('.ctw-bracket-select');
      if (!sel) return;
      const card = sel.closest('.ctw-product');
      if (!card) return;
      const productId = parseInt(card.dataset.productId, 10);
      const product = PRODUCTS.find(p => p.id === productId);
      if (!product) return;
      const optSelects = card.querySelectorAll('.ctw-bracket-select');
      const chosen = {};
      let allChosen = true;
      optSelects.forEach(s => {
        const pos = parseInt(s.dataset.optionPosition, 10);
        if (!s.value) allChosen = false;
        chosen[pos] = s.value;
      });
      const hiddenSel = card.querySelector('.ctw-product__variant-select');
      const addBtn = card.querySelector('.ctw-product__add');
      const statusEl = card.querySelector('.ctw-bracket-status');

      if (!allChosen) {
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Select options first'; }
        if (statusEl) statusEl.textContent = '';
        return;
      }
      // Match the variant against chosen option1 / option2 / option3
      const matching = product.variants.find(v =>
        (v.option1 || '') === (chosen[1] || '') &&
        (v.option2 || '') === (chosen[2] || '') &&
        (v.option3 || '') === (chosen[3] || '')
      );
      if (matching && matching.available) {
        if (hiddenSel) {
          hiddenSel.value = matching.id;
          hiddenSel.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (addBtn) { addBtn.disabled = false; addBtn.textContent = 'Add to Cart'; }
        if (statusEl) { statusEl.textContent = ''; statusEl.classList.remove('is-error'); }
      } else if (matching && !matching.available) {
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Sold out'; }
        if (statusEl) { statusEl.textContent = 'This combination is sold out.'; statusEl.classList.add('is-error'); }
      } else {
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Add to Cart'; }
        if (statusEl) { statusEl.textContent = 'This combination is not available.'; statusEl.classList.add('is-error'); }
      }
    });

    // Multi-variant checkbox changes → persist selection across re-renders
    root.addEventListener('change', (e) => {
      const cb = e.target.closest('.ctw-multi-variant__cb');
      if (!cb) return;
      const productId = parseInt(cb.dataset.productId, 10);
      const variantId = parseInt(cb.dataset.variantId, 10);
      let set = multiVariantMap.get(productId);
      if (!set) { set = new Set(); multiVariantMap.set(productId, set); }
      if (cb.checked) set.add(variantId);
      else set.delete(variantId);
    });

    // Color swatch clicks → sync hidden select + dispatch change
    root.addEventListener('click', (e) => {
      const sw = e.target.closest('.ctw-swatch');
      if (!sw) return;
      e.preventDefault();
      const card = sw.closest('.ctw-product');
      const hiddenSel = card.querySelector('.ctw-product__variant-select');
      if (!hiddenSel) return;
      const variantId = sw.dataset.variantId;
      hiddenSel.value = variantId;
      // Update selected class on swatches
      card.querySelectorAll('.ctw-swatch').forEach(s => s.classList.toggle('is-selected', s === sw));
      hiddenSel.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function bindCartToggle() {
    const cartEl = $('#ctw-cart-panel');
    const toggle = $('#ctw-cart-toggle');
    toggle.addEventListener('click', () => {
      const open = cartEl.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  // iOS leaves visualViewport.offsetTop non-zero after the keyboard closes, which
  // offsets the TAP target of a fixed element from where it's drawn — so taps on
  // the floating cart pill miss. We nudge the pill's real `bottom` so its hit
  // area realigns with its visual position (a small gap above the visible
  // viewport bottom). Settle-debounced (never act on transient mid-scroll
  // geometry); clamps non-negative; NEVER touches the viewport <meta> (that broke
  // the theme header). Mobile only — desktop is the sidebar.
  function bindFloatingCartTouchFix() {
    const cart = $('#ctw-cart-panel');
    const vv = window.visualViewport;
    if (!cart || !vv) return;
    const GAP = 18; // px above the visible viewport bottom (matches the CSS float)
    let t = null, keyboardOpen = false;
    function correct() {
      t = null;
      // Desktop sidebar, keyboard up, or pinch-zoom → defer to the CSS base.
      if (window.innerWidth > 900 || keyboardOpen || Math.abs(vv.scale - 1) > 0.01) {
        cart.style.bottom = '';
        return;
      }
      const cur = parseFloat(cart.style.bottom) || 0;
      const rectBottom = cart.getBoundingClientRect().bottom;
      const target = (vv.offsetTop + vv.height) - GAP;
      const next = Math.max(0, Math.round(cur + (rectBottom - target)));
      if (Math.abs(next - cur) > 1) cart.style.bottom = next + 'px';
    }
    function schedule() { clearTimeout(t); t = setTimeout(correct, 140); }
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('resize', schedule);
    document.addEventListener('focusin', (e) => {
      if (e.target && e.target.matches && e.target.matches('input, select, textarea')) {
        keyboardOpen = true; cart.style.bottom = '';
      }
    });
    document.addEventListener('focusout', () => { keyboardOpen = false; [150, 400, 800].forEach(d => setTimeout(schedule, d)); });
    setTimeout(correct, 120);
  }


  function bindReset() {
    $('#ctw-reset').addEventListener('click', async () => {
      if (!confirm('Clear all your answers and remove everything from your cart?')) return;
      try {
        // Drop the local pending picks and let any in-flight flush land FIRST,
        // so a late flush can't re-populate the cart immediately after clearing.
        cancelPendingAdds();
        if (_drainPromise) { try { await _drainPromise; } catch (e) { /* ignore */ } }
        // Only hit the server if it actually holds items — a purely local
        // session resets with ZERO cart-API calls.
        if (((State.serverCart && State.serverCart.items) || []).length) {
          await clearCart();
          refreshThemeCartNow(); // empty the site cart drawer right away
        } else {
          recomputeMergedCart();
        }
        clearState();
        userVariantMap.clear();
        prevRecMap.clear();
        multiVariantMap.clear();
        // Reset all input values
        $$('input[data-answer]').forEach(el => { el.value = ''; });
        $$('select[data-answer]').forEach(el => { el.value = ''; });
        applyConditionalFields();
        renderAllSections();
        renderCartPanel();
      } catch (e) {
        console.error('[CTW] reset error', e);
        handleCartError(e); // if the cart API is locked, point at the refresh banner
      }
    });
  }

  function bindTooltipsMobile() {
    // Tooltip implementation: a single absolutely-positioned popup appended to
    // <body> so it escapes ancestor overflow/clipping and always fits the
    // viewport. Shown on hover/focus (desktop) and tap (mobile).
    let popup = null;
    function hide() {
      if (popup) { popup.remove(); popup = null; }
    }
    function show(trigger) {
      const text = trigger.getAttribute('data-ctw-tip') || trigger.getAttribute('data-tooltip');
      if (!text) return;
      hide();
      popup = document.createElement('div');
      popup.className = 'ctw-tooltip-popup';
      popup.textContent = text;
      document.body.appendChild(popup);
      // Position above trigger, clamped to viewport
      const r = trigger.getBoundingClientRect();
      const pr = popup.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      let top = r.top + scrollY - pr.height - 10;
      // If no room above, place below
      if (top < scrollY + 8) top = r.bottom + scrollY + 10;
      let left = r.left + scrollX + (r.width / 2) - (pr.width / 2);
      const minLeft = scrollX + 8;
      const maxLeft = scrollX + window.innerWidth - pr.width - 8;
      if (left < minLeft) left = minLeft;
      if (left > maxLeft) left = maxLeft;
      popup.style.top = top + 'px';
      popup.style.left = left + 'px';
      // Position the little arrow under the trigger's center even if the
      // popup got clamped sideways.
      const arrowLeft = (r.left + scrollX + r.width / 2) - left;
      popup.style.setProperty('--arrow-left', arrowLeft + 'px');
    }

    // Desktop: hover / focus
    root.addEventListener('mouseover', (e) => {
      const t = e.target.closest('.ctw-tooltip');
      if (t) show(t);
    });
    root.addEventListener('mouseout', (e) => {
      const t = e.target.closest('.ctw-tooltip');
      if (t) hide();
    });
    root.addEventListener('focusin', (e) => {
      const t = e.target.closest('.ctw-tooltip');
      if (t) show(t);
    });
    root.addEventListener('focusout', (e) => {
      const t = e.target.closest('.ctw-tooltip');
      if (t) hide();
    });
    // Mobile / touch: tap toggles
    root.addEventListener('click', (e) => {
      const t = e.target.closest('.ctw-tooltip');
      if (!t) { hide(); return; }
      e.preventDefault();
      if (popup) hide();
      else show(t);
    });
    // Hide on scroll/resize so it doesn't drift
    window.addEventListener('scroll', hide, { passive: true });
    window.addEventListener('resize', hide);
  }

  // -------------------- Slide-up Quick View modal --------------------
  // Fetches /products/<handle>?view=ctw (a lean layout:false template) and injects
  // the rendered main-product section so the customer can read full product details
  // without leaving the wizard.
  // Build the quick-view add-to-cart bar — title, SKU, Yotpo star rating, price,
  // (variant picker if needed), a qty prefilled from the wizard's recommendation,
  // and an Add button that routes through the SAME deferred local-first cart as
  // every other Add button (no immediate /cart write).
  function buildQuickViewAdd(product, section, onAdded) {
    if (!product || !product.variants || !product.variants.length) return null;
    const variants = visibleVariantsFor(product);
    if (!variants.length) return null;
    let sel = defaultVariantFor(product);
    if (!sel || !variants.some(v => v.id === sel.id)) sel = variants[0];
    // Forms auto-fill the qty when a single package size is recommended (mirrors the card).
    const formAutoQty = formRecQty(product, section);
    const effectiveRec = formAutoQty != null ? formAutoQty : recommendedQtyFor(product, section);
    const initQty = (effectiveRec != null && effectiveRec > 0) ? effectiveRec : 1;
    const multiVariant = variants.length > 1;
    // Full + Half combo (forms only) — a one-click "add both" button, mirroring
    // the product card. The manual dropdown + qty below still do a custom add.
    const combo = section === 'forms' ? formComboPlan(product) : null;

    const wrap = document.createElement('div');
    wrap.className = 'ctw-qv-add' + (combo ? ' is-combo' : '');
    wrap.innerHTML =
      '<div class="ctw-qv-add__info">' +
        '<div class="ctw-qv-add__title">' + escapeHtml(product.title) + '</div>' +
        '<div class="ctw-qv-add__sku" data-qv-sku>' + (sel.sku ? 'SKU: ' + escapeHtml(sel.sku) : '') + '</div>' +
        '<div class="yotpo bottomLine" data-product-id="' + product.id + '"></div>' +
        '<div class="ctw-qv-add__price" data-qv-price>' + escapeHtml(sel.priceFormatted || money(sel.price)) + '</div>' +
      '</div>' +
      (combo ? '<button type="button" class="ctw-qv-add__combo" data-qv-combo>Add recommended package: ' + escapeHtml(combo.text) + '</button>' : '') +
      '<div class="ctw-qv-add__controls">' +
        (multiVariant
          ? '<select class="ctw-qv-add__variant" aria-label="Option">' +
              variants.map(v => '<option value="' + v.id + '"' + (v.id === sel.id ? ' selected' : '') + '>' +
                escapeHtml(v.title) + (v.available ? '' : ' (Sold out)') + '</option>').join('') +
            '</select>'
          : '') +
        '<div class="ctw-qv-add__qtyrow"><label>Qty</label>' +
          '<input type="number" class="ctw-qv-add__qty" min="1" value="' + initQty + '"></div>' +
        '<button type="button" class="ctw-qv-add__btn"' + (sel.available ? '' : ' disabled') + '>' +
          (sel.available ? 'Add to Cart' : 'Out of stock') + '</button>' +
      '</div>';

    const variantSel = wrap.querySelector('.ctw-qv-add__variant');
    const skuEl = wrap.querySelector('[data-qv-sku]');
    const priceEl = wrap.querySelector('[data-qv-price]');
    const qtyEl = wrap.querySelector('.ctw-qv-add__qty');
    const btn = wrap.querySelector('.ctw-qv-add__btn');
    const currentVariant = () => {
      const id = variantSel ? parseInt(variantSel.value, 10) : sel.id;
      return variants.find(v => v.id === id) || sel;
    };
    if (variantSel) {
      variantSel.addEventListener('change', () => {
        const v = currentVariant();
        if (skuEl) skuEl.textContent = v.sku ? 'SKU: ' + v.sku : '';
        if (priceEl) priceEl.textContent = v.priceFormatted || money(v.price);
        btn.disabled = !v.available;
        btn.textContent = v.available ? 'Add to Cart' : 'Out of stock';
      });
    }
    btn.addEventListener('click', () => {
      const v = currentVariant();
      if (!v.available) return;
      if (!runAddCompatibilityChecks(product)) return;
      const qty = Math.max(1, num(qtyEl.value));
      enqueueAdd([{ id: v.id, quantity: qty }]);
      btn.disabled = true;
      btn.classList.add('is-added');
      btn.textContent = '✓ Added';
      // Close the quick view shortly after, revealing the updated mini-cart.
      setTimeout(() => { if (typeof onAdded === 'function') onAdded(); }, 650);
    });
    const comboBtn = wrap.querySelector('[data-qv-combo]');
    if (comboBtn && combo) {
      comboBtn.addEventListener('click', () => {
        if (!runAddCompatibilityChecks(product)) return;
        enqueueAdd([
          { id: combo.fullVariant.id, quantity: combo.fullCount },
          { id: combo.halfVariant.id, quantity: combo.halfCount },
        ]);
        comboBtn.disabled = true;
        comboBtn.classList.add('is-added');
        comboBtn.textContent = '✓ Added';
        setTimeout(() => { if (typeof onAdded === 'function') onAdded(); }, 650);
      });
    }
    return wrap;
  }

  function refreshYotpo() {
    try {
      if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') window.yotpo.refreshWidgets();
      else if (window.yotpoWidgetsContainer && window.yotpoWidgetsContainer.initWidgets) window.yotpoWidgetsContainer.initWidgets();
    } catch (e) { /* widget lib not ready — non-fatal */ }
  }

  // The full Yotpo reviews widget for the bottom of the quick view — the same
  // legacy main widget the product page renders under the description. Yotpo's
  // loader (already on the page; it powers the star ratings) fills it in on
  // refreshWidgets().
  function buildQuickViewReviews(product) {
    if (!product) return null;
    const wrap = document.createElement('div');
    wrap.className = 'ctw-qv-reviews';
    const w = document.createElement('div');
    w.className = 'yotpo yotpo-main-widget';
    w.setAttribute('data-product-id', product.id);
    w.setAttribute('data-name', product.title || '');
    const url = product.url || ('/products/' + product.handle);
    w.setAttribute('data-url', /^https?:/i.test(url) ? url : (window.location.origin + url));
    if (product.image) w.setAttribute('data-image-url', product.image);
    if (product.price != null) w.setAttribute('data-price', (product.price / 100).toFixed(2));
    w.setAttribute('data-currency', (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD');
    wrap.appendChild(w);
    return wrap;
  }

  const quickViewCache = new Map();
  function bindQuickView() {
    const modal = $('#ctw-quickview');
    const body = $('#ctw-quickview-body');
    const loading = $('#ctw-quickview-loading');
    if (!modal || !body) return;

    // Lightbox for gallery thumbnail clicks
    const lightbox = $('#ctw-lightbox');
    const lightboxImg = $('#ctw-lightbox-img');
    function openLightbox(src, alt) {
      if (!lightbox || !lightboxImg) return;
      lightboxImg.src = src;
      lightboxImg.alt = alt || '';
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
    }
    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      if (lightboxImg) lightboxImg.src = '';
    }
    if (lightbox) {
      lightbox.addEventListener('click', closeLightbox);
      const closeBtn = $('#ctw-lightbox-close');
      if (closeBtn) closeBtn.addEventListener('click', (e) => { e.stopPropagation(); closeLightbox(); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !lightbox.hidden) closeLightbox();
      });
    }

    // Intercept clicks on gallery anchors whose href is clearly an image file
    // (the gallery wraps each thumb in an <a href="cdn.shopify.com/.../image.jpg">).
    // Everything else — PDF data sheets, other product links, videos — falls
    // through and behaves natively.
    body.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a || !a.href) return;
      if (/\.(jpe?g|png|webp|gif|avif)(\?|$|#)/i.test(a.href)) {
        e.preventDefault();
        const img = a.querySelector('img');
        openLightbox(a.href, img ? img.alt : '');
      }
    });

    function open(handle, section) {
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => modal.classList.add('is-open'));
      document.documentElement.classList.add('ctw-quickview-locked');
      loading.hidden = false;
      body.innerHTML = '';
      // Set the title in the header from our local product data — fast, no wait for fetch
      const titleEl = $('#ctw-quickview-title');
      const product = PRODUCTS.find(p => p.handle === handle);
      if (titleEl) titleEl.textContent = product ? product.title : 'Loading…';

      const inject = (html) => {
        const fragment = document.createRange().createContextualFragment(html);
        // Prefer the regular main-product section; fall back to the quick-view template node.
        const content = fragment.querySelector('[data-section-type="product"]') ||
                        fragment.querySelector('[data-section-type="main-product"]');
        loading.hidden = true;
        if (content) {
          body.innerHTML = '';
          // 2-column grid: gallery + description share the LEFT column (so they
          // align exactly), and our deferred add-to-cart bar (title, SKU, Yotpo
          // rating, price, qty prefilled from the wizard, Add → mini-cart; the
          // theme's native buy form stays hidden) is the RIGHT column spanning
          // both rows and sticky — bounded to the grid, it stays pinned through
          // the gallery AND description and releases at the reviews (which live
          // outside the grid, full-width below). On mobile the grid collapses to
          // a block and the children stack in DOM order: gallery → add →
          // description. Reviews are appended to the body, after the grid.
          const row = document.createElement('div');
          row.className = 'ctw-qv-row';
          row.appendChild(content);
          const addBar = buildQuickViewAdd(product, section, close);
          if (addBar) row.appendChild(addBar);
          const descEl = content.querySelector('.product-block-list__item--description');
          if (descEl) row.appendChild(descEl);
          body.appendChild(row);
          // Full reviews widget at the bottom (full-width row below everything).
          const reviews = buildQuickViewReviews(product);
          if (reviews) body.appendChild(reviews);
          // Star rating in the add bar → scroll to the reviews. Capture phase +
          // stopPropagation so Yotpo's own (window-based) scroll handler doesn't
          // run — its scroll wouldn't move our modal's scroll container.
          if (addBar && reviews) {
            const stars = addBar.querySelector('.yotpo.bottomLine');
            if (stars) {
              stars.style.cursor = 'pointer';
              stars.addEventListener('click', (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                // Scroll ONLY the modal body. scrollIntoView() walks every
                // scrollable ancestor and was nudging the panel, clipping the
                // fixed header (the X bar) out of view.
                const target = reviews.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop;
                body.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
              }, true);
            }
          }
          // Nudge the theme to re-initialize any non-custom-element scripts on
          // the injected content (custom elements auto-upgrade on insertion).
          try {
            document.dispatchEvent(new CustomEvent('shopify:section:load', {
              bubbles: true,
              detail: { sectionId: content.getAttribute('data-section-id') || 'ctw-quickview', target: content }
            }));
          } catch (err) { /* non-fatal */ }
          refreshYotpo(); // render the star-rating + reviews widgets
        } else {
          body.innerHTML = '<div style="padding:24px">Sorry, we could not load this product. <a href="/products/' + escapeHtml(handle) + '" target="_blank" rel="noopener">Open product page</a>.</div>';
        }
      };

      if (quickViewCache.has(handle)) {
        inject(quickViewCache.get(handle));
        return;
      }
      fetch('/products/' + encodeURIComponent(handle) + '?view=ctw', { credentials: 'same-origin' })
        .then(r => r.ok ? r.text() : Promise.reject(r.status))
        .then(html => { quickViewCache.set(handle, html); inject(html); })
        .catch(err => {
          loading.hidden = true;
          body.innerHTML = '<div style="padding:24px;color:var(--error-color)">Could not load product. <a href="/products/' + escapeHtml(handle) + '" target="_blank" rel="noopener">Open product page</a>.</div>';
          console.error('[CTW] quickview fetch failed', err);
        });
    }

    function close() {
      modal.classList.remove('is-open');
      document.documentElement.classList.remove('ctw-quickview-locked');
      setTimeout(() => {
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        body.innerHTML = '';
      }, 250);
    }

    // Intercept clicks on product image / title anchors → open modal
    root.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-ctw-quickview-open]');
      if (!trigger) return;
      // Let middle-click / cmd-click / ctrl-click open in a new tab normally
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      const handle = trigger.getAttribute('data-ctw-quickview-open');
      const sectionEl = trigger.closest('.ctw-section');
      const section = sectionEl ? sectionEl.dataset.section : null;
      open(handle, section);
    });

    // Close events
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-ctw-quickview-close]')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  // Listen for theme cart drawer changes (so external add-to-cart actions also refresh our panel)
  function bindExternalCartEvents() {
    document.addEventListener('product:added', async () => {
      if (flushInProgress()) return; // our own flush will reconcile when done
      if (Date.now() < _suppressRerenderUntil) return; // echo of our own refresh
      await fetchCart(); // updates serverCart; merged view keeps local picks overlaid
      renderCartPanel();
      renderAllSections();
    });

    // The theme re-renders its mini-cart / cart page by dispatching
    // `cart:rerendered`. This fires whenever the customer removes a line or
    // changes a quantity in the SYSTEM cart drawer — so we re-sync our own
    // panel from the authoritative server cart. The mini-cart variant of this
    // event does NOT bubble, so we listen in the capture phase to catch it.
    let _rerenderSyncTimer = null;
    document.addEventListener('cart:rerendered', () => {
      // Don't fight an add that's still in flight — let it settle first so we
      // don't fetch a mid-update cart. (fetchCart does not re-dispatch this
      // event, so there is no feedback loop.)
      if (_rerenderSyncTimer) clearTimeout(_rerenderSyncTimer);
      _rerenderSyncTimer = setTimeout(async () => {
        _rerenderSyncTimer = null;
        if (flushInProgress()) return; // a flush is mid-flight; it reconciles when done
        if (Date.now() < _suppressRerenderUntil) return; // echo of our own refresh — skip the redundant read
        await fetchCart(); // updates serverCart; merged view keeps local picks overlaid
        renderCartPanel();
        renderAllSections();
      }, 200);
    }, true);
  }

  // -------------------- Init --------------------
  async function init() {
    loadState();
    bindQuestionInputs();
    bindProductActions();
    bindCartToggle();
    bindFloatingCartTouchFix();
    bindReset();
    bindTooltipsMobile();
    bindQuickView();
    bindExternalCartEvents();
    bindFlushBeforeUnload();
    await fetchCart();
    // ---- Restore the local pending picks (the deferred queue is long-lived) ----
    // If the previous view marked a flush attempt on its way out (pagehide
    // keepalive POST), compare the fresh server cart against the snapshot taken
    // at that moment: items that landed are dropped, items that didn't stay
    // pending — no loss, no double-adds.
    //
    // NEVER inside the theme editor or any iframe: pending picks and redirects
    // in the editor's preview frame make the editor refuse to load the page.
    // Keys are left untouched so a real storefront visit still restores them.
    let embedded = !!(window.Shopify && window.Shopify.designMode);
    try { embedded = embedded || window.top !== window.self; } catch (e) { embedded = true; }
    if (!embedded) {
      const saved = loadPersistedPending();
      if (saved) {
        let items = saved.items;
        if (saved.flushedAt) {
          const nowCounts = serverVariantCounts();
          items = items.filter(it =>
            (nowCounts[it.id] || 0) < ((saved.serverCounts[it.id] || 0) + it.quantity)
          );
        }
        if (items.length) {
          console.info('[CTW] restored', items.length, 'pending pick(s)');
          _addQueue = items;
        }
        persistPendingAdds(); // rewrite in normal (un-flushed) form, or clear
        recomputeMergedCart();
      }
    }
    renderAllSections();
    renderCartPanel();
    if (!embedded) {
      // If the customer was headed to the cart/checkout when a challenge
      // interrupted, flush now (the reload solved the challenge) and continue
      // to where they clicked — with all their items.
      const resumeNav = takeResumeNav();
      if (resumeNav && _addQueue.length) {
        await settleCartAdds();
        // If the flush got challenged again, stay here — the banner explains.
        if (!_cartLockedOut) window.location.href = resumeNav;
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
